import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { 
  Loader2,
  Camera,
  Clock,
  Trash2,
  ArrowUp,
  ChevronUp,
  ChevronRight,
  X,
  Mic,
  Square,
  Check,
  SkipForward,
  MessageSquare,
  MapPin,
  Footprints,
  Route,
  AudioLines
} from 'lucide-react';
import { chatAPI, projectAPI, billingAPI, createQueryKey, objectiveMatchAPI, interactiveCardsAPI } from '../services/api';
import StreamingMessage from './StreamingMessage';
import InteractiveCards from './InteractiveCards';
import CardErrorBoundary from './CardErrorBoundary';
import LoadingIndicator from './LoadingIndicator';
import useUnifiedWebSocket from '../hooks/useUnifiedWebSocket';
import useAutoScroll from '../hooks/useAutoScroll';
import usePaginatedMessages from '../hooks/usePaginatedMessages';
import useWelcomeMessage from '../hooks/useWelcomeMessage';
import { useCompass } from '../hooks/useCompass';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useProjectStore } from '../stores';
import uploadService from '../services/uploadService';
import { MicrophoneService } from '../services/MicrophoneService';
import { NativeCameraService } from '../services/NativeCameraService';
import CameraModal from './CameraModal';
import FullscreenImageSpotlightViewer from './FullscreenImageSpotlightViewer';
import { ParticipantNotificationManager } from './ParticipantNotification';
import { useAuth } from '../contexts/AuthContext';
import LocationConsentService from '../services/LocationConsentService';
import { LocationService } from '../services/LocationService';
import { useBillingStore } from '../stores/billingStore';
import VoicePlaybackService from '../services/VoicePlaybackService';
import SoundEffectsService from '../services/SoundEffectsService';
import VoiceRecordingOverlay from './VoiceRecordingOverlay';

// Feature flags / guards
// Client auto-welcome disabled; server handles welcome on project start

const OPTIMISTIC_TIMESTAMP_BUFFER_MS = 250;

const getMessageTimestamp = (message) => {
  try {
    const raw = (message && (message.createdAt || message.created_at || message.timestamp || message.created || message.created_at_ts)) || null;
    if (!raw) return 0;
    if (typeof raw === 'number') return raw;
    return new Date(raw).getTime() || 0;
  } catch (_) {
    return 0;
  }
};

// Guide-card interactions post protocol strings into the chat in the user's
// name ("Guide completed: <title> [Done]") so the AI's history records the
// click. That wire format is load-bearing (anti-spoofing sanitizer + prompts
// reference it) — but humans shouldn't see raw protocol text, so these
// messages render as a compact status chip instead of a user bubble.
const getGuideMarker = (message) => {
  if (message?.role !== 'user') return null;
  const content = (message.content || '').trim();
  let match = content.match(/^Guide completed:\s*(.+?)\s*(?:\[[^\]]*\])?$/);
  if (match) return { kind: 'completed', text: match[1] };
  match = content.match(/^Guide skipped:\s*(.+)$/);
  if (match) return { kind: 'skipped', text: match[1] };
  match = content.match(/^Guide feedback:\s*(.+)$/);
  if (match) return { kind: 'feedback', text: match[1] };
  return null;
};

const GUIDE_MARKER_ICONS = {
  completed: Check,
  skipped: SkipForward,
  feedback: MessageSquare,
};

// Card types that hand the visitor a task (and may anchor the "Go to" waypoint
// row). Evidence/confirmation cards (spotlight, objective_match, plan_update,
// learn) also carry project_objective_id but never direct anyone anywhere.
const WAYPOINT_TASK_CARD_TYPES = ['guide', 'decision', 'observe_photo', 'observe_form', 'observe'];

const GuideMarkerChip = ({ marker }) => {
  const Icon = GUIDE_MARKER_ICONS[marker.kind] || Check;
  return (
    <div className="flex justify-end px-3 sm:px-4 pr-0 py-1 mt-2">
      <span
        className="inline-flex items-center gap-1.5 max-w-[85%] px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-emerald-700 dark:text-emerald-300 text-xs font-medium"
        title={marker.kind === 'completed' ? 'Step completed' : undefined}
      >
        <Icon size={12} className="flex-shrink-0" />
        <span className="truncate">{marker.text}</span>
      </span>
    </div>
  );
};

const createCardEntry = (cards, version = 1, status = 'final') => ({
  cards: Array.isArray(cards) ? [...cards] : [],
  version: typeof version === 'number' ? version : 1,
  status: status || 'final'
});

const extractCardsFromEntry = (entry) => {
  if (!entry) return [];
  if (Array.isArray(entry)) return entry;
  if (Array.isArray(entry.cards)) return entry.cards;
  return [];
};

// Truncated description with expand - keeps header compact
const TruncatedDescription = memo(({ text, maxLines = 2 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      // Check if text overflows (more lines than maxLines)
      const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight) || 20;
      const maxHeight = lineHeight * maxLines;
      setIsTruncated(textRef.current.scrollHeight > maxHeight + 4);
    }
  }, [text, maxLines]);

  return (
    <div className="mt-2">
      <p 
        ref={textRef}
        className={`text-sm text-gray-500 dark:text-gray-400 leading-relaxed ${
          !isExpanded ? 'line-clamp-2' : ''
        }`}
      >
        {text}
      </p>
      {isTruncated && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 mt-1 font-medium"
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
});

const extractVersionFromEntry = (entry) => {
  if (!entry) return 0;
  if (typeof entry === 'object' && typeof entry.version === 'number') return entry.version;
  return Array.isArray(entry) ? 1 : 0;
};


const IsolatedChatInput = memo(({ onSendMessage, isConnected, isGenerating, onStopGeneration, projectId, onSubmittedWithImage = null, externalCommand = null, onExternalCommandHandled = null, isReadOnly = false, voiceAvailable = false, voiceReady = false, voiceMode = false, onToggleVoiceMode = null }) => {
  const [localMessage, setLocalMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [externalAutoSendCapture, setExternalAutoSendCapture] = useState(false);
  const [cameraOverlayHint, setCameraOverlayHint] = useState(null);
  const [pendingExternalMetadata, setPendingExternalMetadata] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const shouldUploadRecordingRef = useRef(true);
  const speechRecognitionRef = useRef(null);
  const speechRecognitionStopRequestedRef = useRef(false);
  const speechWarningShownRef = useRef(false);
  const voiceTranscriptRef = useRef('');
  const committedTranscriptRef = useRef('');
  const isRecordingRef = useRef(false);
  // Outdoor guardrail (workstream V): tap-to-talk clips are bounded — a
  // forgotten open mic on a windy plaza must not stream forever.
  const recordingMaxTimerRef = useRef(null);
  const voiceModeRef = useRef(voiceMode);
  useEffect(() => {
    voiceModeRef.current = voiceMode;
  }, [voiceMode]);
  const isGeneratingRef = useRef(isGenerating);
  useEffect(() => {
    isGeneratingRef.current = isGenerating;
  }, [isGenerating]);
  // ── Recording overlay (walkie-talkie UX) ─────────────────────────────────
  // 'walkie': press-and-hold entry — release sends, slide up locks, slide
  //           sideways cancels. 'locked': tap entry (or slid-up walkie) —
  //           giant Send/Cancel targets. null: no overlay.
  const [recordOverlay, setRecordOverlay] = useState(null);
  const recordOverlayRef = useRef(null);
  useEffect(() => {
    recordOverlayRef.current = recordOverlay;
  }, [recordOverlay]);
  const [micStream, setMicStream] = useState(null);
  const [recordStartedAt, setRecordStartedAt] = useState(null);
  const recordStartedAtRef = useRef(0);
  // Session counter: releasing the finger while the permission prompt is
  // still up must kill the mic the moment it (belatedly) opens.
  const recordSessionRef = useRef(0);
  const overlayWasRecordingRef = useRef(false);
  const walkieOriginRef = useRef(null); // pointer position at walkie entry
  const [walkieDrag, setWalkieDrag] = useState({ dy: 0, dx: 0 });
  const micHoldRef = useRef(null); // mic-button hold detection
  const micHoldTimerRef = useRef(null);
  // Discovery nudge for the voice toggle: visitors don't know the narrated
  // experience exists unless something points at it. Deliberately simple —
  // no per-device "seen" memory (that proved fragile and untestable): every
  // page load where voice is available but OFF shows the nudge for 15s.
  // Evaluated once per mount, so toggling voice off mid-session doesn't
  // re-trigger it.
  const { i18n: nudgeI18n } = useTranslation();
  const inputDe = (nudgeI18n?.language || '').toLowerCase().startsWith('de');
  const [showVoiceNudge, setShowVoiceNudge] = useState(false);
  const voiceNudgeEvaluatedRef = useRef(false);
  useEffect(() => {
    if (voiceNudgeEvaluatedRef.current) return undefined;
    // voiceReady = project settings actually loaded. Before that,
    // voiceAvailable/voiceMode are optimistic defaults — judging on them
    // races the venue's voice_default_on and misfires.
    if (!voiceReady || !voiceAvailable) return undefined;
    voiceNudgeEvaluatedRef.current = true;
    if (voiceModeRef.current || isReadOnly) return undefined;
    setShowVoiceNudge(true);
    const hideTimer = setTimeout(() => setShowVoiceNudge(false), 15000);
    return () => clearTimeout(hideTimer);
  }, [voiceReady, voiceAvailable, isReadOnly]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  // If we hand off a preview URL to the parent for optimistic rendering, we must NOT revoke it here.
  const handedOffPreviewUrlsRef = useRef(new Set());
  const imagePreviewRef = useRef(null);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    imagePreviewRef.current = imagePreview;
  }, [imagePreview]);
  // Handle external commands (e.g., open camera with prefilled text)
  useEffect(() => {
    if (!externalCommand) return;
    try {
      if (externalCommand.type === 'open_camera') {
        const prefillText = externalCommand.prefillText || '';
        setLocalMessage(prefillText);
        setExternalAutoSendCapture(!!externalCommand.autoSend);
        setCameraOverlayHint(externalCommand.overlayHint || null);
        setPendingExternalMetadata(externalCommand.metadata || null);
        setShowCameraModal(true);
      } else if (externalCommand.type === 'prefill_text') {
        const prefillText = externalCommand.prefillText || '';
        setLocalMessage(prefillText);
        setPendingExternalMetadata(externalCommand.metadata || null);
      }
    } finally {
      if (typeof onExternalCommandHandled === 'function') {
        try { onExternalCommandHandled(); } catch (_) {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalCommand]);

  const stopSpeechRecognition = useCallback((options = {}) => {
    const { clearTranscript = false } = options;
    const recognition = speechRecognitionRef.current;
    speechRecognitionStopRequestedRef.current = true;
    if (recognition) {
      try {
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        recognition.stop();
      } catch (_) {}
      speechRecognitionRef.current = null;
    }
    if (clearTranscript) {
      committedTranscriptRef.current = '';
      voiceTranscriptRef.current = '';
      setLocalMessage('');
    }
  }, []);

  const startSpeechRecognition = useCallback(() => {
    if (typeof window === 'undefined') return false;
    const SpeechRecognitionConstructor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionConstructor) {
      if (!speechWarningShownRef.current) {
        toast('Live voice transcription is not supported in this browser. Audio will still be sent.');
        speechWarningShownRef.current = true;
      }
      return false;
    }
    try {
      speechRecognitionStopRequestedRef.current = false;
      const recognition = new SpeechRecognitionConstructor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en-US';
      recognition.onresult = (event) => {
        let committed = committedTranscriptRef.current || '';
        let interimTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const text = result[0]?.transcript || '';
          if (result.isFinal) {
            committed = `${committed} ${text}`.trim();
          } else {
            interimTranscript = text;
          }
        }
        committedTranscriptRef.current = committed.trim();
        const combined = [committedTranscriptRef.current, interimTranscript].filter(Boolean).join(' ').trim();
        voiceTranscriptRef.current = combined;
        if (isRecordingRef.current) {
          setLocalMessage(combined);
        }
      };
      recognition.onerror = (event) => {
        console.warn('Speech recognition error:', event);
      };
      recognition.onend = () => {
        if (!speechRecognitionStopRequestedRef.current) {
          try {
            recognition.start();
          } catch (restartError) {
            console.warn('Failed to restart speech recognition:', restartError);
          }
        }
      };
      recognition.start();
      speechRecognitionRef.current = recognition;
      committedTranscriptRef.current = '';
      voiceTranscriptRef.current = '';
      return true;
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      return false;
    }
  }, []);

  const handleStartRecording = useCallback(async () => {
    if (isReadOnly) return false;
    try {
      // Interruption bridge (workstream V): opening the mic instantly silences
      // the guide — the visitor's voice always wins over narration.
      try { VoicePlaybackService.stop(); } catch (_) {}
      const stream = await MicrophoneService.requestAudioStream({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      shouldUploadRecordingRef.current = true;
      committedTranscriptRef.current = '';
      voiceTranscriptRef.current = '';
      setLocalMessage('');
      setMicStream(stream);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (recordingMaxTimerRef.current) {
          clearTimeout(recordingMaxTimerRef.current);
          recordingMaxTimerRef.current = null;
        }
        const shouldUpload = shouldUploadRecordingRef.current;
        shouldUploadRecordingRef.current = true;
        speechRecognitionStopRequestedRef.current = true;
        // Honest container type: iOS Safari records audio/mp4, Chrome records
        // audio/webm — the old hardcoded 'audio/webm' broke iOS clips when the
        // backend fed them to the model with the wrong mime.
        const actualMime = (mediaRecorder.mimeType || 'audio/webm').split(';')[0];
        const ext = actualMime.includes('mp4')
          ? 'm4a'
          : actualMime.includes('ogg')
            ? 'ogg'
            : 'webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMime });
        // Create file with timestamp for uniqueness
        const filename = `voice_${Date.now()}.${ext}`;
        const audioFile = new File([audioBlob], filename, { type: actualMime });
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        setMicStream(null);
        setRecordStartedAt(null);

      stopSpeechRecognition({ clearTranscript: !shouldUpload });
        const transcriptForMessage = (voiceTranscriptRef.current || '').trim();

        if (!shouldUpload) {
          audioChunksRef.current = [];
          setIsUploading(false);
          setIsRecording(false);
          setLocalMessage('');
          return;
        }

        // Auto submit
        setIsUploading(true);
        try {
            const uploadResult = await uploadService.uploadAudio(audioFile);
            if (uploadResult.success) {
                const audioUrl = uploadResult.data.url;
                const metadata = { audioUrl };
                if (transcriptForMessage) {
                  metadata.voiceTranscript = transcriptForMessage;
                }
                // Voice mode: the agent transcribes the clip itself and the
                // placeholder gets rewritten in place (no emoji — the bubble
                // already carries a mic marker for audio turns).
                const messageText = transcriptForMessage || "Voice message …";
                onSendMessage(messageText, null, metadata); 
                setLocalMessage('');
                voiceTranscriptRef.current = '';
                committedTranscriptRef.current = '';
            } else {
                toast.error(`Audio upload failed: ${uploadResult.error}`);
            }
        } catch (error) {
             console.error("Audio upload error:", error);
             toast.error('Failed to upload audio');
        } finally {
            setIsUploading(false);
            setIsRecording(false); // Ensure state is reset
            audioChunksRef.current = [];
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      isRecordingRef.current = true;
      const startedAtMs = Date.now();
      recordStartedAtRef.current = startedAtMs;
      setRecordStartedAt(startedAtMs);
      // In voice mode the model hears the audio natively — browser speech
      // recognition (V2 replaces it with agent-emitted transcription) would
      // only add a second, worse transcript.
      if (!voiceModeRef.current) {
        startSpeechRecognition();
      }
      // Hard cap per clip: auto-send after 60s of talking.
      recordingMaxTimerRef.current = setTimeout(() => {
        try {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            toast('Recording capped at 60 seconds — sending what we have.');
          }
        } catch (_) {}
      }, 60000);
      return true;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error(MicrophoneService.getPermissionErrorMessage(error));
      return false;
    }
  }, [isReadOnly, onSendMessage, startSpeechRecognition, stopSpeechRecognition]);

  const handleStopRecording = useCallback((options = {}) => {
    const { cancel = false } = options;
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      return;
    }
    shouldUploadRecordingRef.current = !cancel;
    if (recorder.state !== 'inactive') {
      recorder.stop();
    }
    if (cancel) {
      stopSpeechRecognition({ clearTranscript: true });
    }
    setIsRecording(false);
  }, [stopSpeechRecognition]);
  useEffect(() => {
    if (!isRecording || !isGenerating) {
      return;
    }
    handleStopRecording({ cancel: true });
    toast('Voice recording stopped automatically because another action started.');
  }, [isGenerating, isRecording, handleStopRecording]);

  useEffect(() => {
    return () => {
      if (recordingMaxTimerRef.current) {
        clearTimeout(recordingMaxTimerRef.current);
        recordingMaxTimerRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        shouldUploadRecordingRef.current = false;
        try {
          mediaRecorderRef.current.stop();
        } catch (_) {}
      }
      stopSpeechRecognition({ clearTranscript: true });
    };
  }, [stopSpeechRecognition]);

  // ── Recording overlay orchestration ──────────────────────────────────────
  const beginOverlayRecording = useCallback(async (mode) => {
    if (isRecordingRef.current || recordOverlayRef.current) return;
    const session = ++recordSessionRef.current;
    overlayWasRecordingRef.current = false;
    recordStartedAtRef.current = 0;
    setWalkieDrag({ dy: 0, dx: 0 });
    setRecordOverlay(mode);
    recordOverlayRef.current = mode;
    try { navigator.vibrate?.(25); } catch (_) {}
    const ok = await handleStartRecording();
    if (!ok) {
      if (recordSessionRef.current === session) {
        setRecordOverlay(null);
        recordOverlayRef.current = null;
      }
      return;
    }
    if (recordSessionRef.current !== session) {
      // Overlay was dismissed while the mic was still opening (typically the
      // very first use, behind the permission prompt) — don't leave it hot.
      handleStopRecording({ cancel: true });
    }
  }, [handleStartRecording, handleStopRecording]);

  const finishOverlayRecording = useCallback((options = {}) => {
    const { cancel = false } = options;
    recordSessionRef.current += 1; // invalidates any in-flight mic open
    setRecordOverlay(null);
    recordOverlayRef.current = null;
    setWalkieDrag({ dy: 0, dx: 0 });
    if (mediaRecorderRef.current) {
      handleStopRecording({ cancel });
    }
    if (!cancel) {
      try { navigator.vibrate?.(15); } catch (_) {}
    }
  }, [handleStopRecording]);

  // Close the overlay when the recording ends underneath it (60s cap,
  // auto-cancel because generation started, unmount cleanup).
  useEffect(() => {
    if (!recordOverlay) return;
    if (isRecording) {
      overlayWasRecordingRef.current = true;
      return;
    }
    if (overlayWasRecordingRef.current) {
      overlayWasRecordingRef.current = false;
      setRecordOverlay(null);
    }
  }, [isRecording, recordOverlay]);

  // Walkie gesture tracking at document level: the finger that entered the
  // overlay is still down — the (pointer-transparent) overlay just renders.
  useEffect(() => {
    if (recordOverlay !== 'walkie') return undefined;
    const LOCK_PX = 90;
    const CANCEL_PX = 110;
    const onMove = (e) => {
      const origin = walkieOriginRef.current;
      if (!origin) return;
      const dy = origin.y - e.clientY; // up = positive
      const dx = Math.abs(e.clientX - origin.x);
      if (dy > LOCK_PX && dy > dx) {
        setWalkieDrag({ dy: 0, dx: 0 });
        try { navigator.vibrate?.(20); } catch (_) {}
        setRecordOverlay('locked');
        return;
      }
      if (dx > CANCEL_PX && dx > dy) {
        finishOverlayRecording({ cancel: true });
        return;
      }
      setWalkieDrag({ dy: Math.max(0, dy), dx });
    };
    const onRelease = () => {
      // Sub-700ms holds are stray presses, not speech — discard, don't send.
      const startedAt = recordStartedAtRef.current;
      if (!startedAt || Date.now() - startedAt < 700) {
        finishOverlayRecording({ cancel: true });
      } else {
        finishOverlayRecording({ cancel: false });
      }
    };
    // While the finger is down recording, the browser must NOT start a native
    // scroll: a few px of drift (inevitable while walking) would steal the
    // pointer, fire pointercancel and silently kill the recording — the top
    // "it only works sometimes" failure on phones. preventDefault on touchmove
    // (non-passive) keeps the gesture ours; the lock/cancel drags above are
    // computed from pointermove and keep working.
    const onTouchMove = (e) => {
      try { e.preventDefault(); } catch (_) {}
    };
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', onRelease, true);
    document.addEventListener('pointercancel', onRelease, true);
    document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
    return () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onRelease, true);
      document.removeEventListener('pointercancel', onRelease, true);
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
    };
  }, [recordOverlay, finishOverlayRecording]);

  // Press-and-hold ANYWHERE on the chat surface (voice mode only) starts a
  // walkie recording — the power path for someone walking with the phone in
  // one hand. "Anywhere" includes cards, buttons and links: on mobile the
  // viewport is mostly cards, so excluding them degraded the gesture to
  // "hold the slivers between cards" (field finding, Aug 3). A short tap
  // still clicks (the 400ms timer never fires); a matured hold swallows the
  // click that browsers emit on release, so held buttons don't activate.
  // Only text-entry elements and [data-no-ptt] fences (the composer, which
  // has its own mic-hold logic) stay out. Any movement >10px before the
  // threshold reads as scroll intent.
  const surfaceArmRef = useRef(null);
  const surfaceArmTimerRef = useRef(null);
  const surfaceClickSuppressRef = useRef(false);
  useEffect(() => {
    const EXCLUDED =
      'input, textarea, select, [contenteditable="true"], [data-no-ptt]';
    const disarm = () => {
      if (surfaceArmTimerRef.current) {
        clearTimeout(surfaceArmTimerRef.current);
        surfaceArmTimerRef.current = null;
      }
      surfaceArmRef.current = null;
      try {
        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';
        // iOS/Capacitor: long-press image-save / link-preview callout.
        document.body.style.webkitTouchCallout = '';
      } catch (_) {}
    };
    const onDown = (e) => {
      surfaceClickSuppressRef.current = false; // new gesture, new click
      if (!voiceModeRef.current || isReadOnly) return;
      if (isGeneratingRef.current || isRecordingRef.current || recordOverlayRef.current) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest('[data-chat-root]')) return;
      if (t.closest(EXCLUDED)) return;
      surfaceArmRef.current = { x: e.clientX, y: e.clientY };
      try {
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none';
        document.body.style.webkitTouchCallout = 'none';
      } catch (_) {}
      surfaceArmTimerRef.current = setTimeout(() => {
        surfaceArmTimerRef.current = null;
        walkieOriginRef.current = surfaceArmRef.current;
        surfaceArmRef.current = null;
        // The pointerup ending this hold will still fire a click on whatever
        // was held (a card button, a link) — that click means "I was talking",
        // not "activate this". Swallow exactly one.
        surfaceClickSuppressRef.current = true;
        beginOverlayRecording('walkie');
      }, 400);
    };
    const onMove = (e) => {
      if (!surfaceArmTimerRef.current || !surfaceArmRef.current) return;
      const dx = e.clientX - surfaceArmRef.current.x;
      const dy = e.clientY - surfaceArmRef.current.y;
      if (dx * dx + dy * dy > 100) disarm(); // scroll, not a hold
    };
    const onClick = (e) => {
      if (surfaceClickSuppressRef.current) {
        surfaceClickSuppressRef.current = false;
        e.preventDefault();
        e.stopPropagation();
      }
    };
    // Long-press context menus / text selection fight the gesture.
    const onContextMenu = (e) => {
      if (surfaceArmTimerRef.current || recordOverlayRef.current) e.preventDefault();
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', disarm, true);
    document.addEventListener('pointercancel', disarm, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('contextmenu', onContextMenu);
    return () => {
      disarm();
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', disarm, true);
      document.removeEventListener('pointercancel', disarm, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('contextmenu', onContextMenu);
    };
  }, [isReadOnly, beginOverlayRecording]);

  
  const handleChange = useCallback((e) => {
    setLocalMessage(e.target.value);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if ((!localMessage.trim() && !selectedImage) || isGenerating || isUploading) return;
    
    // Generate contextual text if uploading from an observe card
    let messageText = localMessage.trim();
    if (!messageText && selectedImage && pendingExternalMetadata?.selected_action_card) {
      // Auto-generate contextual text for observe card photo uploads
      const cardTitle = pendingExternalMetadata?.action_card_title;
      if (cardTitle) {
        messageText = `Photo for: ${cardTitle}`;
      } else if (
        pendingExternalMetadata?.action_card_type === 'observe' ||
        pendingExternalMetadata?.action_card_type === 'observe_photo'
      ) {
        messageText = 'Observation photo';
      }
    }
    // Send message with optional image.
    // For photos: pass the File + local preview so the parent can render optimistically immediately
    // while uploading in the background.
    if (selectedImage) {
      const previewUrl = imagePreview || URL.createObjectURL(selectedImage);
      handedOffPreviewUrlsRef.current.add(previewUrl);
      setIsUploading(true);
      Promise.resolve(onSendMessage(messageText, { file: selectedImage, previewUrl }, pendingExternalMetadata))
        .catch(() => {})
        .finally(() => setIsUploading(false));
    } else {
      onSendMessage(messageText, null, pendingExternalMetadata);
    }
    
    // Notify parent if image was included
    try {
      if (onSubmittedWithImage && selectedImage) {
        onSubmittedWithImage();
      }
    } catch (_) {}
    
    // Clear form
    setLocalMessage('');
    setSelectedImage(null);
    setImagePreview(null);
    setPendingExternalMetadata(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [localMessage, selectedImage, isGenerating, isUploading, onSendMessage, onSubmittedWithImage, pendingExternalMetadata, imagePreview]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }, [handleSubmit]);

  const handleCameraPhotoCapture = useCallback(async (file, options = { autoSend: false }) => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    // Validate the captured photo
    const validation = uploadService.validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    const shouldAutoSend = Boolean(options?.autoSend) || Boolean(externalAutoSendCapture);

    if (shouldAutoSend && isConnected && !isGenerating) {
      // Auto-send immediately with current text. Parent will upload; we just hand off a preview.
      const previewUrl = URL.createObjectURL(file);
      handedOffPreviewUrlsRef.current.add(previewUrl);
      setIsUploading(true);
      try {
        // Generate contextual text if uploading from an observe card
        let messageText = localMessage.trim();
        if (!messageText && pendingExternalMetadata?.selected_action_card) {
          // Auto-generate contextual text for observe card photo uploads
          const cardTitle = pendingExternalMetadata?.action_card_title;
          if (cardTitle) {
            messageText = `Photo for: ${cardTitle}`;
          } else if (
            pendingExternalMetadata?.action_card_type === 'observe' ||
            pendingExternalMetadata?.action_card_type === 'observe_photo'
          ) {
            messageText = 'Observation photo';
          }
        }
        await onSendMessage(messageText, { file, previewUrl }, pendingExternalMetadata);
        
        // Notify parent that a submission with image occurred
        try {
          if (onSubmittedWithImage) {
            onSubmittedWithImage();
          }
        } catch (_) {}
        // Clear input state
        setLocalMessage('');
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setPendingExternalMetadata(null);
      } catch (err) {
        console.error('Failed to auto-send image:', err);
        toast.error('Failed to send image');
      } finally {
        setIsUploading(false);
        setShowCameraModal(false);
        setExternalAutoSendCapture(false);
        setCameraOverlayHint(null);
      }
    } else {
      // Default behavior: attach image and let user add text and submit
      setSelectedImage(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setShowCameraModal(false);
      setExternalAutoSendCapture(false);
      setCameraOverlayHint(null);
      if (!isConnected) {
        toast.error('Not connected. Image attached — try again in a moment.');
      } else if (isGenerating) {
        toast('Assistant is responding. Image attached — send when ready.');
      }
    }
  }, [isReadOnly, isConnected, isGenerating, onSendMessage, localMessage, onSubmittedWithImage, externalAutoSendCapture, pendingExternalMetadata]);

  const handleImageSelect = useCallback(async () => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }

    if (NativeCameraService.isAvailable()) {
      try {
        const file = await NativeCameraService.openPhotoCapture();
        if (file) {
          await handleCameraPhotoCapture(file, { autoSend: false });
        }
      } catch (error) {
        console.warn('Native camera failed, falling back to web camera modal:', error);
        toast.error('Could not open native camera. Using fallback camera.');
        setShowCameraModal(true);
      }
      return;
    }
    
    setShowCameraModal(true);
  }, [isReadOnly, handleCameraPhotoCapture]);

  const handleCloseCameraModal = useCallback(() => {
    setShowCameraModal(false);
  }, []);


  const handleFileChange = useCallback((e) => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = uploadService.validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = ''; // Clear input
      return;
    }

    setSelectedImage(file);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  }, [isReadOnly]);

  const handleRemoveImage = useCallback(() => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    if (imagePreview && !handedOffPreviewUrlsRef.current.has(imagePreview)) {
      URL.revokeObjectURL(imagePreview);
    }
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [isReadOnly, imagePreview]);

  // Cleanup preview URL on unmount (only if we haven't handed it off to the parent).
  useEffect(() => {
    const handedOff = handedOffPreviewUrlsRef.current;
    return () => {
      try {
        const url = imagePreviewRef.current;
        if (url && !handedOff.has(url)) {
          URL.revokeObjectURL(url);
        }
      } catch (_) {}
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }, [localMessage]);

  return (
    <div className="p-1 px-1.5 mb-3 md:mb-4 w-[95%] max-w-3xl mx-auto border border-gray-300 dark:border-gray-600/30 rounded-3xl shadow bg-white dark:bg-gray-700 text-xs">
      {recordOverlay && (
        <VoiceRecordingOverlay
          mode={recordOverlay}
          stream={micStream}
          startedAt={recordStartedAt}
          drag={walkieDrag}
          onSend={() => finishOverlayRecording({ cancel: false })}
          onCancel={() => finishOverlayRecording({ cancel: true })}
        />
      )}
      {/* data-no-ptt: the composer is the one surface where press-and-hold
          must NOT start a walkie recording — the mic button has its own
          hold logic and the other buttons (camera, send) need plain taps. */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-2" data-no-ptt>
        {/* Image Preview with optional observe_photo context */}
        {imagePreview && (
          <div className="p-3 border-b border-gray-200 dark:border-gray-600">
            {/* Show context header for observe_photo card submissions */}
            {pendingExternalMetadata?.action_card_type === 'observe_photo' && pendingExternalMetadata?.action_card_title && (
              <div className="mb-2 flex items-center gap-2 text-sm">
                <Camera size={14} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-300 font-medium truncate">
                  {pendingExternalMetadata.action_card_title}
                </span>
              </div>
            )}
            <div className="flex items-start gap-3">
              {/* Photo preview */}
              <div className="relative flex-shrink-0">
                <img 
                  src={imagePreview} 
                  alt="Upload preview" 
                  className="w-20 h-20 rounded-lg border border-gray-300 dark:border-gray-600 object-cover"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 text-xs"
                  title="Retake photo"
                >
                  <X size={12} />
                </button>
              </div>
              {/* Hint text for observe_photo submissions — points at the (now
                  green) send button so a staged photo never strands silently */}
              {pendingExternalMetadata?.action_card_type === 'observe_photo' && (
                <div className="flex-1 min-w-0 text-xs text-gray-500 dark:text-gray-400 pt-1">
                  <p>
                    {inputDe
                      ? 'Notiz dazu? Optional — dann den grünen Knopf tippen.'
                      : 'Add a note if you like — then tap the green send button.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Image Upload Button - Left Side */}
          <div className="relative">
            <button
              type="button"
              onClick={handleImageSelect}
              disabled={isReadOnly || !isConnected || isGenerating || isUploading}
              className="p-2 w-10 h-10 rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0 bg-green-200 dark:bg-green-400/20 text-green-600 dark:text-green-500 border border-green-600/10 dark:border-green-500/10"
              title="Add image"
              aria-label="Add image"
            >
              {isUploading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Camera size={20} />
              )}
            </button>
          </div>

          <div className="flex-1 min-w-0 flex items-center">
            <textarea
              ref={textareaRef}
              value={localMessage}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={
                !isConnected
                  ? (inputDe ? 'Verbinde…' : 'Connecting...')
                  : (imagePreview && pendingExternalMetadata?.action_card_type === 'observe_photo')
                    ? (inputDe ? 'Notiz (optional)…' : 'Add a note (optional)...')
                    : (inputDe ? 'Frag mich alles' : 'Ask anything')
              }
              disabled={isReadOnly || !isConnected || isGenerating || isUploading}
              className="w-full py-3 px-1 border-0 resize-none outline-none focus:ring-0 text-sm leading-5 bg-transparent placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white"
              rows={1}
              style={{ minHeight: '20px', maxHeight: '120px' }}
            />
          </div>
          
          <div className="flex items-center gap-2">

            {/* Mic — voice mode only. Tap opens the recording overlay in
                locked mode (giant Send/Cancel); press-and-hold enters walkie
                mode (release to send). All stopping happens in the overlay. */}
            {voiceMode && voiceAvailable && (!isGenerating || isRecording) && (
              <button
                type="button"
                onPointerDown={(e) => {
                  if (isReadOnly || !isConnected || isUploading || isRecording) return;
                  micHoldRef.current = { x: e.clientX, y: e.clientY };
                  micHoldTimerRef.current = setTimeout(() => {
                    micHoldTimerRef.current = null;
                    walkieOriginRef.current = micHoldRef.current;
                    micHoldRef.current = null;
                    beginOverlayRecording('walkie');
                  }, 350);
                }}
                onPointerUp={() => {
                  if (micHoldTimerRef.current) {
                    clearTimeout(micHoldTimerRef.current);
                    micHoldTimerRef.current = null;
                    micHoldRef.current = null;
                    if (!isReadOnly && isConnected && !isUploading && !isRecording) {
                      beginOverlayRecording('locked');
                    }
                  }
                }}
                onPointerLeave={() => {
                  if (micHoldTimerRef.current) {
                    clearTimeout(micHoldTimerRef.current);
                    micHoldTimerRef.current = null;
                    micHoldRef.current = null;
                  }
                }}
                disabled={isReadOnly || !isConnected || (isUploading && !isRecording)}
                className={`p-2 w-10 h-10 rounded-full flex items-center justify-center transition-colors flex-shrink-0 touch-none select-none ${
                  isRecording
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title="Tap or hold to talk"
                aria-label="Start voice recording"
              >
                {isRecording ? <Square size={16} fill="currentColor" /> : <Mic size={18} />}
              </button>
            )}
            
            {/* Voice mode toggle (workstream V): flips the whole conversation
                to ears-first — auto-narrated replies + tap-to-talk mic. */}
            {voiceAvailable && typeof onToggleVoiceMode === 'function' && (
              <div className="relative flex-shrink-0">
                {showVoiceNudge && (
                  <div className="absolute bottom-full right-0 mb-2.5 z-30 w-max max-w-[240px] px-3.5 py-2.5 rounded-xl bg-emerald-600 text-white text-[13px] font-medium leading-snug shadow-xl pointer-events-none animate-bounce">
                    {(nudgeI18n?.language || '').toLowerCase().startsWith('de')
                      ? '🔊 Hör deinem Guide zu — tippe hier'
                      : '🔊 Listen to your guide — tap here'}
                    <div className="absolute top-full right-4 border-[6px] border-transparent border-t-emerald-600" />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowVoiceNudge(false);
                    onToggleVoiceMode();
                  }}
                  disabled={isReadOnly}
                  className={`p-2 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    voiceMode
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  } ${showVoiceNudge ? 'animate-pulse ring-2 ring-emerald-400/70' : ''}`}
                  title={voiceMode ? 'Voice mode on — tap to turn off' : 'Voice mode'}
                  aria-label={voiceMode ? 'Turn voice mode off' : 'Turn voice mode on'}
                  aria-pressed={voiceMode}
                >
                  <AudioLines size={18} />
                </button>
              </div>
            )}

            

            {isGenerating ? (
              <button
                type="button"
                onClick={onStopGeneration}
                className="p-2 bg-red-500 dark:bg-red-600 w-10 h-10 text-white rounded-full hover:bg-red-600 dark:hover:bg-red-500 flex items-center justify-center"
                title="Stop generation"
                aria-label="Stop generation"
              >
                <div className="w-3 h-3 bg-white rounded-sm"></div>
              </button>
            ) : (
              <button
                type="submit"
                disabled={isReadOnly || (!localMessage.trim() && !selectedImage) || !isConnected || isUploading}
                className={`p-2 w-10 h-10 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors ${
                  selectedImage && !isUploading
                    ? 'bg-green-600 hover:bg-green-700 animate-pulse ring-2 ring-green-400/60'
                    : 'bg-gray-500 dark:bg-gray-600 hover:bg-gray-600 dark:hover:bg-gray-500'
                }`}
                aria-label="Send message"
              >
                <ArrowUp size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={uploadService.getSupportedMimeTypes()}
          onChange={handleFileChange}
          className="hidden"
        />
      </form>
      
      {/* Camera Modal */}
      <CameraModal
        isOpen={showCameraModal}
        onClose={handleCloseCameraModal}
        onPhotoCapture={handleCameraPhotoCapture}
        overlayHint={cameraOverlayHint}
      />
    </div>
  );
});

/**
 * Chat History List Component
 */
const ChatHistoryList = memo(({ 
  chats, 
  activeChatId, 
  onChatSelect, 
  onDeleteChat, 
  isLoading 
}) => {

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }

  if (!chats?.length) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
        No nature conversations yet
      </div>
    );
  }

  return (
    <div className="space-y-1 p-2">
      {chats.map((chat) => (
        <div
          key={chat.id}
          className={`flex items-center justify-between p-2 rounded cursor-pointer group transition-colors ${
            activeChatId === chat.id ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
          onClick={() => onChatSelect(chat.id)}
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {chat.title || 'Nature Chat'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <Clock size={10} />
              {formatDistanceToNow(new Date(chat.createdAt), { addSuffix: true })}
            </div>
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteChat(chat.id);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-opacity"
            aria-label="Delete chat"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
    </div>
  );
});

/**
 * Main Chat Section Component
 */
const ChatSection = ({ project, projectId, chatId, setActiveChatId, isReadOnly = false, onAISuggestionsChange = null, disableAutoScroll = false, onRequestFinishProject = null, onObjectiveConfirmed = null, onOpenGoalsModal = null, onSubmittedWithImage = null, onPersistLocationPin = null, onInteractiveCardInteraction = null, onMessagesChange = null, onShowStationOnMap = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const { openPaywall } = useBillingStore((s) => s);
  
  // Stores
  const { activeChatId, setActiveChatId: storeSetActiveChatId } = useProjectStore();
  
  // Use passed props or fallback to params and store
  const { projectId: paramsProjectId, chatId: paramsChatId } = useParams();
  const effectiveProjectId = projectId || paramsProjectId;
  const effectiveChatId = chatId || paramsChatId || activeChatId;
  const effectiveSetActiveChatId = setActiveChatId || storeSetActiveChatId;
  const { data: billingStatusResponse } = useQuery({
    queryKey: ['billingStatus'],
    queryFn: () => billingAPI.getStatus(),
    enabled: !!user,
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
  const tokenBalance = billingStatusResponse?.data?.data?.tokenBalance ?? null;
  
  // Debug logging for read-only and chat ID
  // if (effectiveProjectId && effectiveChatId) {
  //   const willShowInput = !!(effectiveChatId && !isReadOnly);
  //   const willShowBanner = !!(effectiveChatId && isReadOnly);
  //   console.log('[ChatSection] Render check:', {
  //     effectiveChatId,
  //     isReadOnly,
  //     chatIdProp: chatId,
  //     paramsChatId,
  //     activeChatId,
  //     willShowInput,
  //     willShowBanner,
  //     conditionCheck: {
  //       hasChatId: !!effectiveChatId,
  //       notReadOnly: !isReadOnly,
  //       final: willShowInput,
  //       bannerCondition: `effectiveChatId=${!!effectiveChatId} && isReadOnly=${isReadOnly} = ${willShowBanner}`
  //     }
  //   });
    
  //   // Warn if input should be hidden but might still render
  //   if (isReadOnly && effectiveChatId) {
  //     console.warn('[ChatSection] ⚠️ READ-ONLY MODE: Input should be HIDDEN. isReadOnly=', isReadOnly, 'effectiveChatId=', effectiveChatId, 'Banner should show:', willShowBanner);
  //   }
  // }

  // Project query
  const { data: projectQueryData } = useQuery({
    queryKey: createQueryKey.project(effectiveProjectId),
    queryFn: () => projectAPI.getById(effectiveProjectId),
    enabled: !!effectiveProjectId,
    staleTime: 0, // ✅ STALE DATA FIX: Always fetch fresh data for ongoing projects
    refetchOnMount: true, // ✅ STALE DATA FIX: Always refetch when revisiting
    refetchOnWindowFocus: true, // ✅ STALE DATA FIX: Refetch when user returns to tab
    select: (response) => response.data?.data?.project || response.data?.project || response.data
  });
  
  // Use the project from props first, fallback to query data
  const effectiveProject = project || projectQueryData;

  // ── Voice layer (plan_real_guide_gaps.md, workstream V) ──────────────────
  // Listen buttons exist whenever the per-guide kill switch isn't off.
  // Voice MODE (auto-narration + mic) is the visitor's own toggle on top.
  const voiceAvailable =
    !isReadOnly && effectiveProject?.settings?.voice_enabled !== false;
  // Tri-state: true/false = the visitor's own explicit choice (persisted),
  // null = never touched the toggle. With no own choice, the venue's
  // voice_default_on setting decides (headphone experiences start speaking).
  const [voiceMode, setVoiceMode] = useState(() => {
    try {
      const stored = localStorage.getItem('okio_voice_mode');
      if (stored === '1') return true;
      if (stored === '0') return false;
      return null;
    } catch (_) {
      return null;
    }
  });
  const effectiveVoiceMode =
    voiceMode === null
      ? effectiveProject?.settings?.voice_default_on === true
      : voiceMode;
  const voiceModeActive = effectiveVoiceMode && voiceAvailable;
  const voiceModeActiveRef = useRef(voiceModeActive);
  useEffect(() => {
    voiceModeActiveRef.current = voiceModeActive;
    // Earcons live inside voice mode — one switch, no separate setting.
    SoundEffectsService.setEnabled(voiceModeActive);
  }, [voiceModeActive]);
  // Card-arrival earcon: decided ONCE per message, at the first sight of its
  // cards (they arrive via both the interactive_cards stream event and the
  // ai_message_saved metadata). If narration is busy at that moment, the
  // sentence owns the announcement and the ding is permanently suppressed
  // for that message — no late ding after the voice finishes.
  const sfxSeenCardMessagesRef = useRef(new Set());
  const maybeCardArrivalDing = useCallback((messageId, cards) => {
    if (!messageId || !Array.isArray(cards) || cards.length === 0) return;
    if (sfxSeenCardMessagesRef.current.has(messageId)) return;
    const hasPrimary = cards.some(
      (c) => c?.ui_hints?.primary_action === true || c?.type === 'decision'
    );
    if (!hasPrimary) return;
    sfxSeenCardMessagesRef.current.add(messageId);
    SoundEffectsService.cardArrival();
  }, []);
  const sfxPlayedObjectivesRef = useRef(new Set());
  const toggleVoiceMode = useCallback(() => {
    // Flip the EFFECTIVE state (which may come from the venue default), and
    // record the result as the visitor's explicit preference from here on.
    const next = !voiceModeActiveRef.current;
    try {
      localStorage.setItem('okio_voice_mode', next ? '1' : '0');
    } catch (_) {}
    if (next) {
      // The toggle tap IS the unlock gesture mobile autoplay policies need.
      VoicePlaybackService.unlock();
    } else {
      VoicePlaybackService.stop();
    }
    setVoiceMode(next);
  }, []);
  // Nudge evaluation must wait until the project (and thus voice_default_on /
  // voice_enabled) is actually known — not judge against optimistic defaults.
  const voiceSettingsReady = !!effectiveProject;
  // Agent-driven card expansion (ui_action open_card): { cardId, nonce }
  const [externalExpandRequest, setExternalExpandRequest] = useState(null);
  const stationChipInfoRef = useRef(null);
  // Voice clip awaiting the agent's transcription: when the assistant turn
  // arrives with user_transcription, the "🎤 Voice message" placeholder
  // bubble is rewritten in place. { id, metadata } of the user message.
  const pendingAudioUserMessageRef = useRef(null);
  // Refs so the WS-handler effect doesn't re-register when parent callbacks
  // change identity (same pattern as stationChipInfoRef above).
  const openGoalsModalRef = useRef(onOpenGoalsModal);
  const showStationOnMapRef = useRef(onShowStationOnMap);
  useEffect(() => {
    openGoalsModalRef.current = onOpenGoalsModal;
    showStationOnMapRef.current = onShowStationOnMap;
  }, [onOpenGoalsModal, onShowStationOnMap]);
  const voicePlaybackCtx = useMemo(
    () => ({
      enabled: voiceAvailable,
      // Voice MODE drives auto-narration (cards read themselves on expand);
      // `enabled` alone only shows the manual listen buttons.
      voiceModeActive,
      projectId: effectiveProjectId,
      chatId: effectiveChatId,
    }),
    [voiceAvailable, voiceModeActive, effectiveProjectId, effectiveChatId]
  );

  const objectivesSummary = useMemo(() => {
    const objectives = effectiveProject?.objectives || [];
    const total = Array.isArray(objectives) ? objectives.length : 0;
    const completed = Array.isArray(objectives)
      ? objectives.filter((o) => o?.isCompleted === true).length
      : 0;
    return { completed, total };
  }, [effectiveProject?.objectives]);

  // Map-first handoff: pinned stations keyed by objective id, numbered the same
  // way as the map pins (pinned objectives sorted by orderIndex).
  const pinnedStationsById = useMemo(() => {
    const objectives = Array.isArray(effectiveProject?.objectives)
      ? effectiveProject.objectives
      : [];
    const pinned = objectives
      .filter((o) => {
        const geo = o?.objectiveExtraData?.geolocation;
        const lat = Number(geo?.lat);
        const lng = Number(geo?.lng);
        return geo && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
      })
      .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    const byId = {};
    pinned.forEach((o, idx) => {
      const geo = o?.objectiveExtraData?.geolocation || {};
      byId[o.id] = {
        number: idx + 1,
        name: o.objectiveName,
        completed: !!o.isCompleted,
        lat: Number(geo.lat),
        lng: Number(geo.lng),
      };
    });
    return byId;
  }, [effectiveProject?.objectives]);

  // Tour contract (plan_real_guide_gaps.md workstream T): deterministic route
  // shape for on-site routes — stops, walking distance (haversine chain over
  // pinned stations in order), rough duration incl. ~5 min dwell per stop.
  // Mirrors the backend's TOUR SHAPE math in services/llm/geo_awareness.py.
  const tourShape = useMemo(() => {
    if (effectiveProject?.settings?.venue_mode !== 'on_site_route') return null;
    const stations = Object.values(pinnedStationsById).sort((a, b) => a.number - b.number);
    if (stations.length < 2) return null;
    let walkKm = 0;
    for (let i = 1; i < stations.length; i++) {
      const d = (() => {
        const R = 6371;
        const dLat = ((stations[i].lat - stations[i - 1].lat) * Math.PI) / 180;
        const dLng = ((stations[i].lng - stations[i - 1].lng) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos((stations[i - 1].lat * Math.PI) / 180) *
            Math.cos((stations[i].lat * Math.PI) / 180) *
            Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      })();
      if (Number.isFinite(d)) walkKm += d;
    }
    // ×1.25 detour factor: streets aren't straight lines (matches backend
    // ROUTE_DETOUR_FACTOR in geo_awareness.py so UI and agent agree).
    walkKm *= 1.25;
    const mins = Math.max(
      5,
      Math.round(((walkKm * 1000) / 75 + stations.length * 5) / 5) * 5
    );
    return { stops: stations.length, walkKm, mins };
  }, [pinnedStationsById, effectiveProject?.settings?.venue_mode]);

  const handleLocationCardCollapsed = useCallback(() => {
    if (typeof onAISuggestionsChange === 'function') {
      // console.log('🗺️ Hiding location pins for collapsed card');
      onAISuggestionsChange([]);
    }
  }, [onAISuggestionsChange]);
  
  // Local state
  const [activeTab, setActiveTab] = useState('chat');
  const [isGenerating, setIsGenerating] = useState(false);
  // When non-message actions (e.g. objective match confirm/dismiss) trigger an agent follow-up,
  // we still want the global chat loading spinner to appear.
  const objectiveActionTimeoutRef = useRef(null);
  const handlePlanUpdateComplete = useCallback((_info = null) => {
    // Plan updates schedule an automatic agent follow-up a few seconds later.
    // Show the global spinner during this gap so the UX doesn't feel "dead".
    setIsGenerating(true);
    if (objectiveActionTimeoutRef.current) {
      clearTimeout(objectiveActionTimeoutRef.current);
    }
    objectiveActionTimeoutRef.current = setTimeout(() => {
      setIsGenerating(false);
      objectiveActionTimeoutRef.current = null;
    }, 20000);
  }, []);

  const [optimisticMessage, setOptimisticMessage] = useState(null);
  const optimisticMessageRef = useRef(null);
  useEffect(() => {
    optimisticMessageRef.current = optimisticMessage;
  }, [optimisticMessage]);
  const optimisticTimestampMapRef = useRef(new Map());
  const latestChronologicalTimestampRef = useRef(0);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const [isReplacingStreamingMessage, setIsReplacingStreamingMessage] = useState(false);
  // Show a spinner after text streaming completes while waiting for cards to arrive/persist
  const [awaitingCardsMessageId, setAwaitingCardsMessageId] = useState(null);
  const [, setScrollLocked] = useState(false);
  // Removed client-side chat creation state
  // Removed client-side chat creation timestamp ref
  const connectedAtRef = useRef(null);
  // Consolidated state for better performance
  const [chatState, setChatState] = useState({
    messageCards: new Map(),
    croppedImages: new Map(), // LEGACY - for backward compat
    userLocation: null,
    objectiveNotifications: [],
    // ISSUE 3 FIX: Track auto-dismissed card IDs for immediate UI update
    // (backend is updated via API, but we need local state for React to re-render)
    autoDismissedCardIds: new Set()
  });
  
  // Compass heading from shared hook (Device Orientation API - works even when stationary)
  const { heading: compassHeading } = useCompass();
  const compassHeadingRef = useRef(null);
  useEffect(() => {
    compassHeadingRef.current = compassHeading;
  }, [compassHeading]);
  
  // Extract state values for use in callbacks
  const { messageCards, croppedImages, userLocation, objectiveNotifications, autoDismissedCardIds } = chatState;
  const messageCardsRef = useRef(messageCards);
  useEffect(() => {
    messageCardsRef.current = messageCards;
  }, [messageCards]);

  // Memoized haversine calculation to avoid recreating on every render
  const haversineKm = useCallback((lat1, lng1, lat2, lng2) => {
    if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Handle location card expansion/collapse for map integration
  const handleLocationCardExpanded = useCallback((locations) => {
    if (typeof onAISuggestionsChange === 'function' && locations && Array.isArray(locations)) {
      const suggestions = locations.map((location, idx) => {
        const lat = Number(location.location.lat);
        const lng = Number(location.location.lng);
        const distKm = (userLocation && typeof userLocation.lat === 'number' && typeof userLocation.lng === 'number')
          ? haversineKm(userLocation.lat, userLocation.lng, lat, lng)
          : null;
        return {
          id: `location-expanded-${location.id || idx}-${lat.toFixed(6)}-${lng.toFixed(6)}`,
          name: location.name || 'Location',
          distance: distKm,
          distanceText: distKm != null ? `${distKm.toFixed(1)}km` : 'Nearby',
          description: location.description || '',
          source: 'ai-temp',
          coordinates: { lat, lng },
          confidence: 0.9,
          fullMatch: undefined
        };
      });

      // console.log(`🗺️ Showing ${suggestions.length} location pins for expanded card:`, suggestions.map(s => `${s.name} (${s.id})`));
      onAISuggestionsChange(suggestions);
    }
  }, [haversineKm, userLocation, onAISuggestionsChange]);
  
  // Timeout/guard refs
  const fallbackTimeoutRef = useRef(null);
  const scrollLockTimeoutRef = useRef(null);
  const welcomeTimeoutRef = useRef(null);
  const streamingIdRef = useRef(null);

  // Auto-scroll functionality (must be before other hooks that depend on refs)
  const { 
    containerRef,
    endRef,
    scrollToBottom,
    autoScrollOnNewContent,
    autoScrollOnInitialLoad,
    autoScrollOnStreaming,
    autoScrollOnStreamingComplete,
    forceScrollToBottom,
    isUserScrolledUp: userHasScrolled,
    isNearBottom,
    resetScrollState,
    handleScroll
  } = useAutoScroll();

  

  // Server state (persisted messages)
  const {
    messages: serverMessages,
    isLoading: isMessagesLoading,
    loadMore,
    hasMore,
    isLoadingMore,
    addMessage,
    updateMessage
  } = usePaginatedMessages(effectiveChatId || null);

  const lastPersistedServerTimestamp = useMemo(() => {
    const list = Array.isArray(serverMessages) ? serverMessages : [];
    if (!list.length) return 0;
    return getMessageTimestamp(list[list.length - 1]);
  }, [serverMessages]);

  // Unified message list: server state + client state
  const allMessages = useMemo(() => {
    const serverList = Array.isArray(serverMessages) ? [...serverMessages] : [];
    const clientList = [];
    
    // Debug: Track serverMessages changes
    // if (process.env.NODE_ENV !== 'production') {
    //   console.debug('[msgs] serverMessages changed', { 
    //     count: serverList.length,
    //     messages: serverList.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 30) + '...' }))
    //   });
    // }
    
    // Add streaming message (client state)
    if (streamingMessage && streamingMessage.id && !serverList.find(m => m && m.id === streamingMessage.id)) {
      // Use a stable timestamp based on the last server message to prevent reordering during streaming
      const lastServerMessage = serverList[serverList.length - 1];
      const baseTimestamp = lastServerMessage?.createdAt ? new Date(lastServerMessage.createdAt).getTime() : Date.now();
      const stableTimestamp = new Date(baseTimestamp + 100).toISOString(); // 100ms after last message
      
      clientList.push({
        id: streamingMessage.id,
        role: 'assistant',
        content: streamingMessage.content || '',
        createdAt: stableTimestamp,
        chatId: effectiveChatId,
        _isStreaming: true
      });
    }
    
    // Add optimistic message (client state) - already has future timestamp
    // But only if there's no matching server message with the same client_message_id or ID
    if (optimisticMessage) {
      const clientMessageId = optimisticMessage.client_message_id;
      const optimisticId = optimisticMessage.id;
      
      const hasMatchingServerMessage = serverList.some(m => {
        if (!m) return false;
        // Match by client_message_id (most reliable for optimistic messages)
        if (clientMessageId && m.client_message_id === clientMessageId && m.role === optimisticMessage.role) {
          return true;
        }
        // Also match by ID if optimistic message has a real ID (shouldn't happen, but defensive)
        if (optimisticId && m.id === optimisticId && m.role === optimisticMessage.role) {
          return true;
        }
        return false;
      });
      
      // Only add optimistic message if no matching server message exists
      if (!hasMatchingServerMessage) {
        clientList.push({
          ...optimisticMessage,
          _isOptimistic: true
        });
      }
    }
    
    // Combine server and client state
    const combined = [...serverList, ...clientList];
    
    return combined.sort((a, b) => {
      const ta = getMessageTimestamp(a);
      const tb = getMessageTimestamp(b);
      if (ta !== tb) return ta - tb;
      
      // Tiebreaker: streaming messages come before saved messages with same timestamp
      if (a._isStreaming && !b._isStreaming) return -1;
      if (!a._isStreaming && b._isStreaming) return 1;
      
      // Tiebreaker: optimistic messages go last
      if (a._isOptimistic && !b._isOptimistic) return 1;
      if (!a._isOptimistic && b._isOptimistic) return -1;
      
      // Final tiebreaker: ID (stable ordering)
      const ida = a?.id || '';
      const idb = b?.id || '';
      return ida.localeCompare(idb);
    });
  }, [serverMessages, streamingMessage, optimisticMessage, effectiveChatId]);

  // Newest assistant message id — gates per-message UX that must only fire on
  // the live turn (auto-expanding a single action card, auto-image spinner).
  const latestAssistantMessageId = useMemo(() => {
    for (let i = allMessages.length - 1; i >= 0; i--) {
      if (allMessages[i]?.role === 'assistant') return allMessages[i].id;
    }
    return null;
  }, [allMessages]);

  // Ref for allMessages to use in callbacks without stale closure issues
  const allMessagesRef = useRef(allMessages);
  useEffect(() => {
    allMessagesRef.current = allMessages;
  }, [allMessages]);

  // The tour contract renders once, under the tour's opening message.
  const firstAssistantMessageId = useMemo(() => {
    if (!Array.isArray(allMessages)) return null;
    const first = allMessages.find((m) => m?.role === 'assistant');
    return first?.id || null;
  }, [allMessages]);

  // Map-first handoff row: the most recent assistant message that hands the
  // visitor a TASK at a pinned, uncompleted station gets the "Go to" waypoint.
  // Only task cards anchor it — evidence/confirmation cards (spotlight finds,
  // objective updates, learn sidecars) also carry project_objective_id but
  // aren't sending anyone anywhere, so they fall through to the earlier task
  // message instead of re-issuing the directive on a confirmation turn.
  const stationChipInfo = useMemo(() => {
    if (typeof onShowStationOnMap !== 'function' || isReadOnly) return null;
    if (!Array.isArray(allMessages)) return null;
    for (let i = allMessages.length - 1; i >= 0; i--) {
      const message = allMessages[i];
      if (message?.role !== 'assistant') continue;
      const cards = message?.message_metadata?.interactive_cards;
      if (!Array.isArray(cards)) continue;
      const linked = cards.find(
        (card) =>
          WAYPOINT_TASK_CARD_TYPES.includes(card?.type) &&
          typeof card?.project_objective_id === 'string' &&
          card.project_objective_id
      );
      if (!linked) continue;
      const station = pinnedStationsById[linked.project_objective_id];
      // Latest station already checked off (or not pinned) → no chip.
      if (!station || station.completed) return null;
      return {
        messageId: message.id,
        objectiveId: linked.project_objective_id,
        number: station.number,
        name: station.name,
        lat: station.lat,
        lng: station.lng,
      };
    }
    return null;
  }, [allMessages, pinnedStationsById, onShowStationOnMap, isReadOnly]);

  // Voice ui_action open_map needs the current station without re-registering
  // websocket handlers on every message — ref mirror.
  useEffect(() => {
    stationChipInfoRef.current = stationChipInfo;
  }, [stationChipInfo]);

  // ── Live arrival geofence (plan_real_guide_gaps.md — "the guide is blind
  // between messages") ──────────────────────────────────────────────────────
  // While a pinned, uncompleted station is the active target ("Go to" chip
  // visible), watch the live position. Crossing INTO the station radius —
  // after having been seen outside it, with a decent fix, held for a short
  // dwell — fires exactly one backend arrival turn: the guide speaks because
  // you arrived. The same fixes keep chatState.userLocation fresh, which is
  // what flips the chip to "You're here" in real time. No new UI artifacts.
  const isGeneratingRef = useRef(isGenerating);
  useEffect(() => {
    isGeneratingRef.current = isGenerating;
  }, [isGenerating]);
  // Per-target crossing state + per-mount fired set (server dedupes across
  // reloads via persisted history, so in-memory is enough here).
  const arrivalTrackRef = useRef({ objectiveId: null, seenOutside: false, insideSince: null });
  const arrivalFiredRef = useRef(new Set());
  const arrivalLocUpdateAtRef = useRef(0);

  const handleArrivalFix = useCallback((pos) => {
    const { latitude, longitude, accuracy } = pos?.coords || {};
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    const now = Date.now();

    // Keep the station chip / distance labels live (throttled — a fix every
    // second must not re-render the whole chat every second).
    if (now - arrivalLocUpdateAtRef.current > 3000) {
      arrivalLocUpdateAtRef.current = now;
      const loc = { lat: latitude, lng: longitude };
      if (Number.isFinite(accuracy)) loc.accuracy = Math.round(accuracy);
      if (typeof compassHeadingRef.current === 'number') loc.heading = compassHeadingRef.current;
      setChatState((prev) => ({ ...prev, userLocation: loc }));
    }

    const target = stationChipInfoRef.current;
    if (!target || !Number.isFinite(target.lat) || !Number.isFinite(target.lng)) return;

    if (arrivalTrackRef.current.objectiveId !== target.objectiveId) {
      arrivalTrackRef.current = { objectiveId: target.objectiveId, seenOutside: false, insideSince: null };
    }
    const track = arrivalTrackRef.current;
    if (arrivalFiredRef.current.has(target.objectiveId)) return;
    // A coarse fix is not arrival evidence (mirrors the backend gate).
    if (Number.isFinite(accuracy) && accuracy > 150) return;

    const km = haversineKm(latitude, longitude, target.lat, target.lng);
    if (!Number.isFinite(km)) return;
    const meters = km * 1000;

    // Hysteresis: arrive at ≤200 m, only count as "outside" beyond 275 m, so
    // GPS wobble across the boundary can't re-arm or spam the state machine.
    if (meters > 275) {
      track.seenOutside = true;
      track.insideSince = null;
      return;
    }
    if (meters > 200) return; // in the hysteresis band — hold state
    // Opened the page already at the station → the first turn's geo context
    // already says HERE; an arrival beat would be noise. Crossing only.
    if (!track.seenOutside) return;
    if (track.insideSince == null) {
      track.insideSince = now;
      return;
    }
    if (now - track.insideSince < 8000) return; // dwell: a drive-by GPS bounce isn't an arrival
    if (isGeneratingRef.current) return; // let the current turn finish; next fix retries

    arrivalFiredRef.current.add(target.objectiveId);
    try { if (navigator.vibrate) navigator.vibrate([30, 40, 30]); } catch (_) {}
    // Earcon into silence (service ducks under narration + gates on voice mode).
    try { SoundEffectsService.cardArrival(); } catch (_) {}
    projectAPI
      .reportStationArrival(effectiveProjectId, target.objectiveId, {
        chat_id: effectiveChatId,
        lat: latitude,
        lng: longitude,
        ...(Number.isFinite(accuracy) ? { accuracy: Math.round(accuracy) } : {}),
        voice_mode: !!voiceModeActiveRef.current,
      })
      .catch(() => {
        // Network hiccup: re-arm so a later fix can retry (server dedupes).
        arrivalFiredRef.current.delete(target.objectiveId);
      });
  }, [haversineKm, effectiveProjectId, effectiveChatId]);
  const handleArrivalFixRef = useRef(handleArrivalFix);
  useEffect(() => {
    handleArrivalFixRef.current = handleArrivalFix;
  }, [handleArrivalFix]);

  useEffect(() => {
    const objectiveId = stationChipInfo?.objectiveId;
    if (!objectiveId || isReadOnly || !effectiveProjectId || !effectiveChatId) return undefined;
    if (!LocationConsentService.canAccessLocation()) return undefined;
    let cancelled = false;
    let watch = null;
    (async () => {
      try {
        watch = await LocationService.watchPosition(
          (pos) => { try { handleArrivalFixRef.current(pos); } catch (_) {} },
          null,
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
        );
        if (cancelled && watch) { try { watch.clear(); } catch (_) {} }
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
      if (watch) { try { watch.clear(); } catch (_) {} }
    };
  }, [stationChipInfo?.objectiveId, isReadOnly, effectiveProjectId, effectiveChatId]);

  // Collect all objective match IDs from messages to fetch statuses (deduplicated).
  // Important: avoid depending on rapidly-mutating per-message UI state (e.g. streaming/optimistic maps),
  // otherwise we can accidentally spam the status endpoint during streaming.
  const allObjectiveMatchIds = useMemo(() => {
    const ids = new Set();

    const collectMatchIds = (card) => {
      if (!card) return;

      // Single match cards
      if (card.type === 'objective_match' && Array.isArray(card.actions)) {
        card.actions.forEach(action => {
          if (action?.matchId) ids.add(action.matchId);
        });
      }

      // Multi-match cards contain nested cards under `matches`
      if (card.type === 'multi_objective_match' && Array.isArray(card.matches)) {
        card.matches.forEach(nestedCard => collectMatchIds(nestedCard));
      }
    };

    // Check all messages for objective match cards (server-persisted metadata only)
    if (Array.isArray(allMessages)) {
      allMessages.forEach(message => {
        const cards = Array.isArray(message?.message_metadata?.interactive_cards)
          ? message.message_metadata.interactive_cards
          : [];
        cards.forEach(collectMatchIds);
      });
    }

    return Array.from(ids);
  }, [allMessages]);

  // Fetch objective match statuses once for all messages (deduplicated)
  const [objectiveMatchStatuses, setObjectiveMatchStatuses] = useState({});
  const objectiveMatchStatusFetchStateRef = useRef({ lastAt: 0, lastKey: '' });
  useEffect(() => {
    let isCancelled = false;
    let timeoutId = null;
    const now = Date.now();
    const idsKey = (Array.isArray(allObjectiveMatchIds) ? [...allObjectiveMatchIds] : [])
      .sort()
      .join(',');

    // Throttle + debounce to avoid hammering the backend during streaming/renders.
    // We still refetch immediately on explicit confirm/dismiss actions elsewhere.
    const MIN_FETCH_INTERVAL_MS = 8000;
    const state = objectiveMatchStatusFetchStateRef.current;
    if (idsKey && state.lastKey === idsKey && (now - state.lastAt) < MIN_FETCH_INTERVAL_MS) {
      return () => { isCancelled = true; };
    }

    const fetchStatuses = async () => {
      if (!allObjectiveMatchIds || allObjectiveMatchIds.length === 0) {
        setObjectiveMatchStatuses({});
        return;
      }
      try {
        // Mark fetch moment/key for throttling
        state.lastAt = Date.now();
        state.lastKey = idsKey;

        const res = await objectiveMatchAPI.getStatuses(allObjectiveMatchIds);
        const data = res?.data?.data || {};
        if (!isCancelled) {
          setObjectiveMatchStatuses(data);
        }
      } catch (err) {
        // Silent fail; keep UI interactive
        if (!isCancelled) {
          setObjectiveMatchStatuses({});
        }
      }
    };

    // Debounce slightly to coalesce bursty message updates.
    timeoutId = setTimeout(fetchStatuses, 350);
    return () => {
      isCancelled = true;
      if (timeoutId) {
        try { clearTimeout(timeoutId); } catch (_) {}
      }
    };
  }, [allObjectiveMatchIds]);

  useEffect(() => {
    if (!Array.isArray(allMessages) || allMessages.length === 0) return;
    const last = getMessageTimestamp(allMessages[allMessages.length - 1]);
    if (last > latestChronologicalTimestampRef.current) {
      latestChronologicalTimestampRef.current = last;
    }
  }, [allMessages]);

  useEffect(() => {
    latestChronologicalTimestampRef.current = 0;
    optimisticTimestampMapRef.current.clear();
    setOptimisticMessage(null);
  }, [effectiveChatId]);


  // Auto-load earlier messages on scroll-up near top (after hooks initialized)
  useEffect(() => {
    const el = containerRef?.current;
    if (!el || isReadOnly) return;
    let lastTriggeredAt = 0;
    const MIN_INTERVAL_MS = 400;
    const onScrollTopLoad = () => {
      try {
        const now = Date.now();
        if (el.scrollTop <= 60 && hasMore && !isLoadingMore) {
          if (now - lastTriggeredAt >= MIN_INTERVAL_MS) {
            lastTriggeredAt = now;
            loadMore(containerRef);
          }
        }
      } catch (_) {}
    };
    el.addEventListener('scroll', onScrollTopLoad, { passive: true });
    return () => {
      try { el.removeEventListener('scroll', onScrollTopLoad); } catch (_) {}
    };
  }, [isReadOnly, containerRef, hasMore, isLoadingMore, loadMore]);

  // WebSocket connection with streaming handlers (disabled for read-only)
  const {
    isConnected,
    sendMessage: sendWebSocketMessage,
    streamingMessage: wsStreamingMessage,
    registerHandler
  } = useUnifiedWebSocket(isReadOnly ? null : effectiveChatId);

  // Welcome message handling with fallbacks (disabled in read-only mode)
  const {
    isWaitingForWelcome,
    welcomeTimeout,
    retryWelcomeMessage,
    clearWelcomeTimeout
  } = useWelcomeMessage(isReadOnly ? null : effectiveChatId, effectiveProjectId, isReadOnly ? false : isConnected);

  // Track when the websocket first becomes connected for readiness gating
  useEffect(() => {
    if (isConnected) {
      if (!connectedAtRef.current) {
        connectedAtRef.current = Date.now();
      }
    } else {
      connectedAtRef.current = null;
    }
  }, [isConnected]);

  // Update local streaming message state from WebSocket
  useEffect(() => {
    if (wsStreamingMessage && wsStreamingMessage.id) {
      setStreamingMessage(wsStreamingMessage);
      // if (process.env.NODE_ENV !== 'production') {
      //   try { console.debug('[msgs] streaming update', { id: wsStreamingMessage.id, len: (wsStreamingMessage.content || '').length }); } catch (_) {}
      // }
    } else if (wsStreamingMessage && wsStreamingMessage.isComplete) {
      // Clear streaming message when complete
      setStreamingMessage(null);
      // if (process.env.NODE_ENV !== 'production') {
      //   try { console.debug('[msgs] streaming complete'); } catch (_) {}
      // }
    }
  }, [wsStreamingMessage]);

  // Track latest streaming message id for migration logic
  useEffect(() => {
    if (streamingMessage && streamingMessage.id) {
      streamingIdRef.current = streamingMessage.id;
      // if (process.env.NODE_ENV !== 'production') {
      //   try { console.debug('[msgs] set streamingIdRef', { id: streamingMessage.id }); } catch (_) {}
      // }
    }
  }, [streamingMessage]);

  // RACE CONDITION FIX: Removed proactive refetch that was causing message reordering
  // Instead, rely on user_message_saved/ai_message_saved events to append/replace messages
  // This prevents the race between streaming, refetch, and optimistic message insertion
  // useEffect(() => {
  //   const newId = wsStreamingMessage?.id;
  //   const prevId = streamingIdRef.current;
  //   if (!newId || !prevId || newId === prevId) return;
  //   const prevExists = Array.isArray(allMessages) && allMessages.some(m => m && m.id === prevId);
  //   if (!prevExists && effectiveChatId) {
  //     try {
  //       queryClient.invalidateQueries({ queryKey: createQueryKey.chat(effectiveChatId) });
  //       if (process.env.NODE_ENV !== 'production') {
  //         try { console.debug('[msgs] invalidated chat to pull previous streaming', { prevId, newId }); } catch (_) {}
  //       }
  //     } catch (_) {}
  //   }
  // }, [wsStreamingMessage?.id, allMessages, effectiveChatId, queryClient]);

  // WebSocket message handlers
  useEffect(() => {
    if (!registerHandler) return undefined;

    const disposers = [];
    const trackHandler = (type, handler) => {
      const dispose = registerHandler(type, handler);
      if (typeof dispose === 'function') {
        disposers.push(dispose);
      }
    };

    const onUserMessageSaved = (data) => {
      try {
        if (!data?.message_id) return;

        const clientMessageId = data?.client_message_id;
        const storedOptimisticTs = clientMessageId ? optimisticTimestampMapRef.current.get(clientMessageId) : null;
        const optimisticTimestamp = storedOptimisticTs || optimisticMessageRef.current?.createdAt;
        const createdAt = optimisticTimestamp || data.timestamp || new Date().toISOString();

        // Update optimistic message ID immediately to match server ID
        // This ensures React uses the same key and doesn't remount the component
        // Then clear it since the server message will replace it
        if (clientMessageId && optimisticMessageRef.current?.client_message_id === clientMessageId) {
          // If the optimistic message used a local blob preview URL, revoke it now that the
          // persisted message is arriving (it will use a real URL).
          try {
            const prev = optimisticMessageRef.current;
            const prevUrl = prev?.message_metadata?.imageUrl || prev?.metadata?.imageUrl;
            if (prevUrl && typeof prevUrl === 'string' && prevUrl.startsWith('blob:')) {
              URL.revokeObjectURL(prevUrl);
            }
          } catch (_) {}
          setOptimisticMessage((current) => {
            if (!current || current.client_message_id !== clientMessageId) return current;
            // Update ID to server ID so React key stays stable, then clear immediately
            // The server message will seamlessly replace it
            return null;
          });
        } else if (!clientMessageId) {
          setOptimisticMessage(null);
        }

        // Add the real message (addMessage will deduplicate by client_message_id)
        addMessage({
          id: data.message_id,
          role: 'user',
          content: data.content || '',
          createdAt,
          chatId: effectiveChatId,
          message_metadata: data.metadata || {},
          client_message_id: clientMessageId
        });

        latestChronologicalTimestampRef.current = Math.max(
            latestChronologicalTimestampRef.current,
            getMessageTimestamp({ createdAt })
        );

        // Clean up timestamp map
        if (clientMessageId) {
          optimisticTimestampMapRef.current.delete(clientMessageId);
        }

        // Audio clip without a local transcript: remember it so the agent's
        // transcription (on the assistant turn) can replace the placeholder.
        const meta = data.metadata || {};
        if (meta.audioUrl && !meta.voiceTranscript) {
          pendingAudioUserMessageRef.current = { id: data.message_id, metadata: meta };
        }
      } catch (_) {}
    };
    trackHandler('user_message_saved', onUserMessageSaved);

    const onAiMessageSaved = (data) => {
      setIsGenerating(false);
      if (objectiveActionTimeoutRef.current) {
        clearTimeout(objectiveActionTimeoutRef.current);
        objectiveActionTimeoutRef.current = null;
      }
      try { clearWelcomeTimeout && clearWelcomeTimeout(); } catch (_) {}
      setIsReplacingStreamingMessage(true);
      setScrollLocked(true);

      const aiTimestampMs = data?.timestamp ? new Date(data.timestamp).getTime() : Date.now();
      const safeTimestampMs = Math.max(aiTimestampMs, latestChronologicalTimestampRef.current + 1);
      const createdAt = new Date(safeTimestampMs).toISOString();

      try {
        if (data?.message_id) {
          addMessage({
            id: data.message_id,
            role: 'assistant',
            content: data.content || '',
            createdAt,
            chatId: effectiveChatId,
            message_metadata: data.metadata || {}
          });
          latestChronologicalTimestampRef.current = Math.max(
            latestChronologicalTimestampRef.current,
            safeTimestampMs
          );
        }
      } catch (_) {}

      // Voice layer: the agent heard the clip — swap the user's
      // "🎤 Voice message" placeholder for the actual words, in place.
      // (Backend persists the same rewrite for history reloads.)
      try {
        const heard = data?.metadata?.user_transcription;
        const pending = pendingAudioUserMessageRef.current;
        if (heard && pending?.id) {
          updateMessage(pending.id, {
            content: heard,
            message_metadata: { ...(pending.metadata || {}), voiceTranscript: heard },
          });
          pendingAudioUserMessageRef.current = null;
        }
      } catch (_) {}

      // Voice mode: the welcome turn is generated server-side WITHOUT the
      // client's voice-mode state (no voice fields in its schema, so no
      // narration event ever fires for it). If the visitor has voice mode on,
      // narrate it from here. On fresh page loads autoplay may be blocked —
      // the playback service then parks it and plays on the first tap.
      try {
        if (
          data?.metadata?.welcome_message &&
          voiceModeActiveRef.current &&
          data?.content &&
          data?.message_id
        ) {
          VoicePlaybackService.speak({
            text: data.content,
            key: `msg_${data.message_id}`,
            projectId: effectiveProjectId,
            chatId: effectiveChatId,
            messageId: data.message_id,
          });
        }
      } catch (_) {}

      // Voice ui_action (workstream V): the agent's spoken turn can drive the
      // screen — server-sanitized (enum whitelist, card_index resolved to a
      // real card id). Executed only while the visitor is in voice mode.
      try {
        const uiAction = data?.metadata?.ui_action;
        if (uiAction && voiceModeActiveRef.current) {
          if (uiAction.action === 'open_card' && uiAction.card_id) {
            setExternalExpandRequest({ cardId: uiAction.card_id, nonce: Date.now() });
          } else if (uiAction.action === 'show_goals' && typeof openGoalsModalRef.current === 'function') {
            openGoalsModalRef.current();
          } else if (uiAction.action === 'open_map' && typeof showStationOnMapRef.current === 'function') {
            const oid = stationChipInfoRef.current?.objectiveId;
            if (oid) showStationOnMapRef.current(oid);
          }
        }
      } catch (_) {}

      // ✅ FIX: Populate messageCards immediately if metadata has them
      // This fixes the "Spinner never disappears" issue by ensuring we don't wait for cards that are already here
      try {
        if (data?.message_id && data?.metadata?.interactive_cards && Array.isArray(data.metadata.interactive_cards)) {
           const cards = data.metadata.interactive_cards;
           maybeCardArrivalDing(data.message_id, cards);
           const incomingVersion = typeof data.metadata.interactive_cards_version === 'number'
            ? data.metadata.interactive_cards_version
            : 1;
           const status = data?.metadata?.response_status || 'final';
           // Important: cards can legitimately be [] (e.g., response_mode=chat_only).
           // Treat empty arrays as a final "no cards" state so we clear the post-stream spinner.
           setChatState(prev => {
              const prevEntry = prev.messageCards.get(data.message_id);
              if (extractVersionFromEntry(prevEntry) >= incomingVersion) {
                return prev;
              }
              const updated = new Map(prev.messageCards);
              updated.set(data.message_id, createCardEntry(cards, incomingVersion, status));
              return { ...prev, messageCards: updated };
           });
        }
      } catch (_) {}

      try {
        const streamingId = streamingIdRef.current;
        const savedId = data?.message_id;
        if (streamingId && savedId && streamingId !== savedId) {
          setChatState(prev => {
            const migrated = new Map(prev.messageCards);
            if (migrated.has(streamingId)) {
              const cardsForStream = migrated.get(streamingId);
              if (cardsForStream && !migrated.has(savedId)) {
                migrated.set(savedId, cardsForStream);
              }
              migrated.delete(streamingId);
              return { ...prev, messageCards: migrated };
            }
            return prev;
          });
        }
      } catch (_) {}

      try {
        if (data?.message_id) {
          setAwaitingCardsMessageId(data.message_id);
          // Increase timeout to 15s to allow for slow card generation (e.g. vision analysis)
          setTimeout(() => {
            setAwaitingCardsMessageId(prev => {
              if (prev === data.message_id && !messageCardsRef.current?.has(data.message_id)) {
                return null;
              }
              return prev;
            });
          }, 15000);
        }
      } catch (_) {}

      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
      }
      fallbackTimeoutRef.current = setTimeout(() => {
        setStreamingMessage(null);
        setIsReplacingStreamingMessage(false);
        setScrollLocked(false);
        fallbackTimeoutRef.current = null;
      }, 3000);
    };
    trackHandler('ai_message_saved', onAiMessageSaved);

    const onGenerationStopped = () => {
      setIsGenerating(false);
      if (objectiveActionTimeoutRef.current) {
        clearTimeout(objectiveActionTimeoutRef.current);
        objectiveActionTimeoutRef.current = null;
      }
      setStreamingMessage(null);
    };
    trackHandler('generation_stopped', onGenerationStopped);

    // Voice layer: the spoken track streams in BEFORE the rest of the turn
    // (schema-ordered) — start narration immediately, killing the dead air.
    const onVoiceTrack = (data) => {
      const voiceTrack = data?.voice_track;
      if (!voiceTrack || !voiceModeActiveRef.current) return;
      VoicePlaybackService.speak({
        text: voiceTrack,
        key: `msg_${data?.message_id || 'live'}`,
        projectId: effectiveProjectId,
        chatId: effectiveChatId,
        messageId: data?.message_id || null,
      });
    };
    trackHandler('voice_track', onVoiceTrack);

    const onInteractiveCards = (data) => {
      if (!data?.message_id || !Array.isArray(data?.cards)) return;
      maybeCardArrivalDing(data.message_id, data.cards);
      const incomingVersion = typeof data.version === 'number' ? data.version : 1;
      const status = data?.status || 'final';
      setChatState(prev => {
        const existing = prev.messageCards.get(data.message_id);
        if (extractVersionFromEntry(existing) >= incomingVersion) {
          return prev;
        }
        const next = new Map(prev.messageCards);
        next.set(data.message_id, createCardEntry(data.cards, incomingVersion, status));
        return { ...prev, messageCards: next };
      });
    };
    const onCroppedImagesReady = (data) => {
      if (data?.message_id && data?.crops) {
        setChatState(prev => ({
          ...prev,
          croppedImages: new Map(prev.croppedImages).set(data.message_id, data.crops)
        }));
      }
    };
    trackHandler('interactive_cards', onInteractiveCards);
    trackHandler('cropped_images_ready', onCroppedImagesReady);

    const onObjectiveProgress = async (data) => {
      if (data?.confirmedBy) {
        const objectiveTitle = data?.objectiveTitle || 'an objective';
        const matchId = data?.matchId;
        const dedupeKey = matchId
          ? `objective_progress:${matchId}`
          : `objective_progress:${data?.objectiveId || objectiveTitle}:${data?.confirmedBy?.id}`;
        // Reward earcon — once per completion, voice-mode gated in the service.
        if (!sfxPlayedObjectivesRef.current.has(dedupeKey)) {
          sfxPlayedObjectivesRef.current.add(dedupeKey);
          SoundEffectsService.success();
        }
        setChatState(prev => {
          const alreadyQueued = prev.objectiveNotifications.some(n => n._key === dedupeKey);
          if (alreadyQueued) return prev;
          return {
            ...prev,
            objectiveNotifications: [
              ...prev.objectiveNotifications,
              {
                user: data.confirmedBy,
                action: 'objective_fulfilled',
                objectiveTitle,
                id: `obj-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
                _key: dedupeKey
              }
            ]
          };
        });

        if (effectiveProjectId && queryClient) {
          // Invalidate and refetch project query to update objectives list with fresh completion state
          await queryClient.invalidateQueries(createQueryKey.project(effectiveProjectId));
          await queryClient.refetchQueries(createQueryKey.project(effectiveProjectId));
        }
      }
    };
    trackHandler('objective_progress', onObjectiveProgress);

    // Progress evidence (non-completion): keep multiplayer clients in sync by refreshing project state.
    // Backend emits: { type: "objective_progress_evidence", data: { projectId, userMessageId, updates: [...] } }
    const onObjectiveProgressEvidence = async (data) => {
      try {
        const updates = data?.updates || data?.data?.updates || [];
        if (!Array.isArray(updates) || updates.length === 0) return;
        const first = updates[0] || {};
        const contributedByUserId = first?.contributedByUserId;
        const contributedBy = first?.contributedBy;
        const objectiveTitle = first?.objectiveName || 'an objective';
        const ackText = (first?.ack_text || '').trim();

        // Show a lightweight notification for other participants (no new cards).
        // We don't have full user profiles for progress events, so display a neutral label.
        if (contributedByUserId && user?.id && String(contributedByUserId) !== String(user.id)) {
          const dedupeKey = `objective_progress_evidence:${contributedByUserId}:${first?.projectObjectiveId || objectiveTitle}`;
          setChatState(prev => {
            const alreadyQueued = prev.objectiveNotifications.some(n => n._key === dedupeKey);
            if (alreadyQueued) return prev;
            return {
              ...prev,
              objectiveNotifications: [
                ...prev.objectiveNotifications,
                {
                  user: (contributedBy && typeof contributedBy === 'object')
                    ? contributedBy
                    : { id: contributedByUserId, username: 'Teammate' },
                  action: 'objective_progress_logged',
                  objectiveTitle,
                  objectiveProgress: ackText || null,
                  id: `objprog-${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
                  _key: dedupeKey
                }
              ]
            };
          });
        }

        if (effectiveProjectId && queryClient) {
          await queryClient.invalidateQueries(createQueryKey.project(effectiveProjectId));
          await queryClient.refetchQueries(createQueryKey.project(effectiveProjectId));
        }
      } catch (e) {
        // Non-fatal
      }
    };
    trackHandler('objective_progress_evidence', onObjectiveProgressEvidence);

    const onError = (data) => {
      console.error('WebSocket error:', data);
      
      // ISSUE 1 FIX: Suppress toast for "task already running" errors during welcome message generation
      // This happens when the welcome message is being generated and another action triggers simultaneously
      const isTaskAlreadyRunningError = 
        data?.task_already_running === true || 
        data?.error === 'task_already_running' ||
        (typeof data?.message === 'string' && (
          data.message.includes('already being processed') ||
          data.message.includes('try again')
        ));
      
      if (isTaskAlreadyRunningError) {
        // Silent handling - the backend is already processing, just wait
        console.debug('[ChatSection] Suppressing task-already-running toast - welcome message or concurrent request in progress');
      } else {
        toast.error(data?.message || 'An error occurred');
      }
      
      setIsGenerating(false);
      if (objectiveActionTimeoutRef.current) {
        clearTimeout(objectiveActionTimeoutRef.current);
        objectiveActionTimeoutRef.current = null;
      }
      setStreamingMessage(null);
    };
    trackHandler('error', onError);

    return () => {
      disposers.forEach((dispose) => {
        try { dispose(); } catch (_) {}
      });
    };
  }, [registerHandler, effectiveChatId, clearWelcomeTimeout, addMessage, updateMessage, queryClient, effectiveProjectId, user?.id, maybeCardArrivalDing]);

  // Compass heading is now managed by the shared useCompass hook
  // No need for duplicate listener setup here

  // 🗺️ Get user location for AI suggestions
  useEffect(() => {
    if (navigator.geolocation && LocationConsentService.canAccessLocation()) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          if (typeof position.coords.accuracy === 'number' && Number.isFinite(position.coords.accuracy)) {
            location.accuracy = Math.round(position.coords.accuracy);
          }
          // Include compass heading from Device Orientation API (works even when stationary)
          if (compassHeadingRef.current !== null && typeof compassHeadingRef.current === 'number') {
            location.heading = compassHeadingRef.current;
          }
          // Fallback to geolocation heading if compass not available (only when device is moving)
          else if (typeof position.coords.heading === 'number' && !Number.isNaN(position.coords.heading) && position.coords.heading >= 0) {
            location.heading = ((position.coords.heading % 360) + 360) % 360;
          }
          setChatState(prev => ({
            ...prev,
            userLocation: location
          }));
          // console.log('📍 Got user location for AI suggestions:', position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.warn('📍 map for AI suggestions:', error.code, error.message);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 600000 // 10 minute cache
        }
      );
    }
  }, []);

  // Chat queries are handled by usePaginatedMessages hook
  // No need for duplicate chat query

  const { data: chatsData, isLoading: isChatsLoading } = useQuery({
    queryKey: createQueryKey.chats(effectiveProjectId),
    queryFn: () => chatAPI.getByProject(effectiveProjectId),
    enabled: !!effectiveProjectId,
    staleTime: 30000, // Optimized: Consider data fresh for 30s since chat list rarely changes
    refetchOnWindowFocus: false, // Optimized: Disable aggressive refetching for chat list
    refetchOnMount: true, // ✅ STALE DATA FIX: Always refetch when revisiting
    select: (response) => response.data?.data?.chats || []
  });

  

  // Notify parent component of messages changes for useMessagePins
  useEffect(() => {
    if (onMessagesChange && allMessages) {
      onMessagesChange(allMessages);
    }
  }, [allMessages, onMessagesChange]);

  // Fullscreen image viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  // External command for IsolatedChatInput (to support chatless camera flows)
  const [externalCommand, setExternalCommand] = useState(null);
  const pendingInitialMessageSentRef = useRef(null);

  const [viewerImageUrl, setViewerImageUrl] = useState('');
  const [viewerSpotlights, setViewerSpotlights] = useState([]);
  const [highlightedSpotlightId, setHighlightedSpotlightId] = useState(null);

  const openViewerForMessage = useCallback(({ message }) => {
    const imageUrl = message?.message_metadata?.imageUrl || message?.metadata?.imageUrl;
    if (!imageUrl) return;

    // First try: backlink via assistant metadata
    let associatedAssistantMessageId = null;
    if (allMessages && allMessages.length > 0) {
      const userMessageId = message.id;
      const assistantForThisImage = allMessages.find(m => {
        if (m.role !== 'assistant') return false;
        const meta = m?.message_metadata || {};
        return (meta.source_user_message_id === userMessageId) || (meta.source_image_url && meta.source_image_url === imageUrl);
      });
      if (assistantForThisImage) {
        associatedAssistantMessageId = assistantForThisImage.id;
      }
    }

    // Fallback heuristic: next assistant message after this user message that has spotlights
    if (!associatedAssistantMessageId && allMessages && allMessages.length > 0) {
      const idx = allMessages.findIndex(m => m.id === message.id);
      if (idx !== -1) {
        for (let i = idx + 1; i < allMessages.length; i++) {
          const candidate = allMessages[i];
          if (candidate?.role === 'assistant' && messageCards?.has(candidate.id)) {
            const cards = extractCardsFromEntry(messageCards.get(candidate.id));
            if (cards.some(c => c.type === 'spotlight')) {
              associatedAssistantMessageId = candidate.id;
              break;
            }
          }
        }
      }
    }

    // Fallback: if nothing found in history, use streaming assistant message (if present with spotlights)
    if (!associatedAssistantMessageId && streamingMessage?.id && messageCards?.has(streamingMessage.id)) {
      const cards = extractCardsFromEntry(messageCards.get(streamingMessage.id));
      if (cards.some(c => c.type === 'spotlight')) {
        associatedAssistantMessageId = streamingMessage.id;
      }
    }

    // Extract spotlights only from the associated assistant message
    let spotlights = [];
    if (associatedAssistantMessageId && messageCards?.has(associatedAssistantMessageId)) {
      const cards = extractCardsFromEntry(messageCards.get(associatedAssistantMessageId));
      spotlights = cards.filter(c => c.type === 'spotlight');
      
      // Debug logging for spotlight bbox investigation
      if (process.env.NODE_ENV !== 'production' && spotlights.length > 0) {
        console.debug('[ChatSection] Spotlight data for viewer:', {
          messageId: associatedAssistantMessageId,
          spotlights: spotlights.map(s => ({
            id: s.id,
            title: s.title,
            bounding_box: s.bounding_box,
            bounding_boxes: s.bounding_boxes
          }))
        });
      }
    }

    // console.log('[ChatSection] Opening viewer with:', {
    //   imageUrl,
    //   spotlightsCount: spotlights.length,
    //   spotlights: spotlights.map(s => ({ id: s.id, title: s.title, bbox: s.bounding_box }))
    // });
    
    setViewerImageUrl(imageUrl);
    setViewerSpotlights(spotlights);
    setViewerOpen(true);
  }, [allMessages, messageCards, streamingMessage]);

  const closeViewer = useCallback(() => {
    setViewerOpen(false);
    setViewerImageUrl('');
    setViewerSpotlights([]);
    setHighlightedSpotlightId(null);
  }, []);

  // Memoized message processing to avoid unnecessary reprocessing
  const processedMessages = useMemo(() => {
    if (!allMessages || allMessages.length === 0) {
      return { newCardsMap: new Map(), newCroppedImagesMap: new Map() };
    }
    
    const newCardsMap = new Map();
    const newCroppedImagesMap = new Map();
    
    allMessages.forEach(message => {
      // Check if message has interactive cards in metadata
      const cards = message?.message_metadata?.interactive_cards;
      if (cards && Array.isArray(cards) && cards.length > 0) {
        // Ensure learn_content is included in cards for persistence
        const enrichedCards = cards.map(card => ({
          ...card,
          learn_content: card.learn_content || null
        }));

        const version = typeof message?.message_metadata?.interactive_cards_version === 'number'
          ? message.message_metadata.interactive_cards_version
          : 1;
        const status = message?.message_metadata?.response_status || 'final';
        newCardsMap.set(message.id, createCardEntry(enrichedCards, version, status));
      }
      
      // Check if message has cropped images in metadata (LEGACY)
      const croppedImagesData = message?.message_metadata?.cropped_images;
      if (croppedImagesData && typeof croppedImagesData === 'object') {
        newCroppedImagesMap.set(message.id, croppedImagesData);
      }
    });
    
    return { newCardsMap, newCroppedImagesMap };
  }, [allMessages]);

  // Resolve the correct source image URL for a given assistant message id
  const getSourceImageForAssistantMessage = useCallback((assistantMessageId) => {
    try {
      if (!assistantMessageId || !Array.isArray(allMessages)) return null;
      const assistantMsg = allMessages.find(m => m && m.id === assistantMessageId);
      const aMeta = assistantMsg?.message_metadata || {};
      if (aMeta.source_image_url) {
        return aMeta.source_image_url;
      }
      if (aMeta.source_user_message_id) {
        const userMsg = allMessages.find(m => m && m.id === aMeta.source_user_message_id && m.role === 'user');
        const url = userMsg?.message_metadata?.imageUrl || userMsg?.metadata?.imageUrl;
        if (url) return url;
      }
      const idx = allMessages.findIndex(m => m && m.id === assistantMessageId);
      if (idx > 0) {
        for (let i = idx - 1; i >= 0; i--) {
          const candidate = allMessages[i];
          const url = candidate?.role === 'user'
            ? (candidate?.message_metadata?.imageUrl || candidate?.metadata?.imageUrl)
            : null;
          if (url) return url;
        }
      }
      const recentUserWithImage = [...allMessages].reverse().find(m =>
        m?.role === 'user' && ((m?.message_metadata?.imageUrl) || (m?.metadata?.imageUrl))
      );
      return recentUserWithImage?.message_metadata?.imageUrl || recentUserWithImage?.metadata?.imageUrl || null;
    } catch (_) {
      return null;
    }
  }, [allMessages]);

  // Helper function for shallow comparison of arrays/objects
  const shallowEqual = useCallback((a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => item === b[index]);
    }
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every(key => a[key] === b[key]);
    }
    return false;
  }, []);

  // Extract cards, cropped images, and bounding boxes from loaded messages and populate state
  useEffect(() => {
    const { newCardsMap, newCroppedImagesMap } = processedMessages;
    
    // Update messageCards with a merge strategy to avoid flicker when websocket-provided cards arrive before persistence
    if (newCardsMap.size > 0 || messageCards.size > 0) {
      setChatState(prev => {
        const merged = new Map(prev.messageCards);
        let changed = false;
        for (let [key, value] of newCardsMap) {
          const prevVal = merged.get(key);
          const incomingVersion = extractVersionFromEntry(value);
          const currentVersion = extractVersionFromEntry(prevVal);
          if (incomingVersion > currentVersion) {
            merged.set(key, value);
            changed = true;
          }
        }
        if (changed) {
          return { ...prev, messageCards: merged };
        }
        return prev;
      });
    }
    
    // Update croppedImages if there are changes
    if (newCroppedImagesMap.size > 0 || croppedImages.size > 0) {
      setChatState(prev => {
        // Check if maps are different
        if (prev.croppedImages.size !== newCroppedImagesMap.size) {
          return { ...prev, croppedImages: newCroppedImagesMap };
        }
        
        // Check if content is different using shallow comparison
        for (let [key, value] of newCroppedImagesMap) {
          if (!prev.croppedImages.has(key) || !shallowEqual(prev.croppedImages.get(key), value)) {
            return { ...prev, croppedImages: newCroppedImagesMap };
          }
        }
        
        return prev; // No changes
      });
    }
  }, [processedMessages, messageCards.size, croppedImages.size, shallowEqual]);

  // Debug: Track changes to allMessages
  // useEffect(() => {
  //   console.log('🔍 allMessages changed:', {
  //     count: allMessages?.length || 0,
  //     messages: allMessages?.map(m => ({ id: m.id, role: m.role, content: m.content?.substring(0, 50) + '...' })) || []
  //   });
  // }, [allMessages]);

  // Infinite scroll for loading more messages is handled by usePaginatedMessages hook
  // No need for duplicate useInfiniteScroll hook

  // Auto-scroll effects
  // 1. Auto-scroll on initial load when messages are first loaded
  // Track if we've done initial scroll for this chat
  const hasInitialScrolledRef = useRef(false);
  
  useEffect(() => {
    // Reset scroll flag when chat changes
    hasInitialScrolledRef.current = false;
  }, [effectiveChatId]);
  
  useEffect(() => {
    if (!disableAutoScroll && allMessages && allMessages.length > 0 && !hasInitialScrolledRef.current) {
      // console.log('🚀 Auto-scroll: Initial load with', allMessages.length, 'messages');
      hasInitialScrolledRef.current = true;
      
      // Use a small delay to ensure DOM is fully rendered, especially for images
      setTimeout(() => {
        if (autoScrollOnInitialLoad) {
          autoScrollOnInitialLoad();
        }
      }, 50);
    }
  }, [allMessages, autoScrollOnInitialLoad, disableAutoScroll]);

  // 2. Auto-scroll when optimistic message is added (user sends message)
  // Track if last message had an image to trigger additional scroll attempts
  const lastMessageHadImageRef = useRef(false);
  
  useEffect(() => {
    if (!disableAutoScroll && optimisticMessage) {
      // console.log('🚀 Auto-scroll: Optimistic message added');
      
      // Check if this message has an image
      const hasImage = optimisticMessage?.metadata?.imageUrl || optimisticMessage?.message_metadata?.imageUrl;
      lastMessageHadImageRef.current = hasImage;
      
      if (autoScrollOnNewContent) {
        autoScrollOnNewContent('smooth');
        
        // For images, add additional scroll attempts as they load
        if (hasImage) {
          // console.log('🖼️ Message has image, scheduling additional scroll attempts');
          setTimeout(() => {
            if (autoScrollOnNewContent && !userHasScrolled) {
              autoScrollOnNewContent('smooth');
            }
          }, 150);
          
          setTimeout(() => {
            if (autoScrollOnNewContent && !userHasScrolled) {
              autoScrollOnNewContent('smooth');
            }
          }, 400);
          
          // Final attempt after image should be loaded
          setTimeout(() => {
            if (autoScrollOnNewContent && !userHasScrolled) {
              autoScrollOnNewContent('smooth');
            }
          }, 800);
        }
      }
    }
  }, [optimisticMessage, autoScrollOnNewContent, disableAutoScroll, userHasScrolled]);

  // 3. Auto-scroll during streaming (when content is being generated)
  useEffect(() => {
    if (!disableAutoScroll && streamingMessage?.content && !isReplacingStreamingMessage && !userHasScrolled) {
      // console.log('🚀 Auto-scroll: Streaming content update, length:', streamingMessage.content.length);
      if (autoScrollOnStreaming) {
        autoScrollOnStreaming();
      }
    }
  }, [streamingMessage?.content, autoScrollOnStreaming, isReplacingStreamingMessage, disableAutoScroll, userHasScrolled]);

  // 4. Auto-scroll when generation state changes (loading indicator appears/disappears)
  useEffect(() => {
    if (!disableAutoScroll && isGenerating && !userHasScrolled) {
      // console.log('🚀 Auto-scroll: Generation started, showing loading indicator');
      if (autoScrollOnNewContent) {
        // Small delay to ensure the loading indicator has rendered
        setTimeout(() => {
          if (!userHasScrolled) {
            // console.log('🚀 Auto-scroll: Delayed scroll for loading indicator');
            autoScrollOnNewContent('smooth');
          }
        }, 100);
      }
    }
  }, [isGenerating, autoScrollOnNewContent, disableAutoScroll, userHasScrolled]);

  // 5. Force auto-scroll when chat ID changes (switching chats)
  useEffect(() => {
    if (!disableAutoScroll && effectiveChatId && resetScrollState) {
      // console.log('🚀 Auto-scroll: Chat changed, resetting scroll state');
      resetScrollState();
      // Also reset the initial scroll flag for the new chat
      hasInitialScrolledRef.current = false;
    }
  }, [effectiveChatId, resetScrollState, disableAutoScroll]);

  // 6. Clear streaming message only when final message appears in allMessages (with timeout safety)
  useEffect(() => {
    if (!disableAutoScroll && isReplacingStreamingMessage && streamingMessage?.id && allMessages) {
      const finalMessageExists = allMessages.find(msg => msg.id === streamingMessage.id);
      if (finalMessageExists) {
        // console.log('🔄 Final message now rendered, clearing streaming message');
        
        // Clear the fallback timeout since we found the final message
        if (fallbackTimeoutRef.current) {
          clearTimeout(fallbackTimeoutRef.current);
          fallbackTimeoutRef.current = null;
        }
        
        // Preserve scroll position during transition
        const preserveScrollPosition = () => {
          if (containerRef.current) {
            const container = containerRef.current;
            const scrollBefore = container.scrollTop;
            const scrollHeightBefore = container.scrollHeight;
            
            // Clear streaming message
            setStreamingMessage(null);
            
            // Use multiple RAF to ensure DOM is updated
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (container) {
                  const scrollHeightAfter = container.scrollHeight;
                  const heightDifference = scrollHeightAfter - scrollHeightBefore;
                  
                  // Adjust scroll position to compensate for any height changes
                  container.scrollTop = scrollBefore + heightDifference;
                  
                  // Clear replacement flag and scroll lock after a longer delay
                  setTimeout(() => {
                    setIsReplacingStreamingMessage(false);
                    setScrollLocked(false);
                    // Only do final scroll if we're near the bottom
                    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
                      container.scrollTop = container.scrollHeight;
                    }
                  }, 200); // Increased delay to prevent competing scrolls
                }
              });
            });
          } else {
            // Fallback if no container
            setStreamingMessage(null);
            setTimeout(() => {
              setIsReplacingStreamingMessage(false);
              setScrollLocked(false);
            }, 200);
          }
        };
        
        preserveScrollPosition();
      }
      // Safety timeout: if final message never appears, clear replacement state to avoid stuck spinner
      const safety = setTimeout(() => {
        setIsReplacingStreamingMessage(false);
        setScrollLocked(false);
      }, 2000);
      return () => clearTimeout(safety);
    }
  }, [isReplacingStreamingMessage, streamingMessage?.id, allMessages, containerRef, disableAutoScroll]);

  // 6b. When cards arrive for the current streaming or just-saved assistant message, nudge to bottom if near bottom
  useEffect(() => {
    if (disableAutoScroll) return;
    const currentAssistantId = streamingMessage?.id || awaitingCardsMessageId;
    if (!currentAssistantId) return;
    if (!messageCards.has(currentAssistantId)) return;
    if (userHasScrolled || isReplacingStreamingMessage) return;
    if (typeof isNearBottom === 'function' && !isNearBottom()) return;
    if (typeof autoScrollOnStreamingComplete === 'function') {
      setTimeout(() => {
        autoScrollOnStreamingComplete();
      }, 50);
    } else if (typeof scrollToBottom === 'function') {
      setTimeout(() => scrollToBottom('smooth'), 50);
    }
  }, [disableAutoScroll, messageCards, streamingMessage?.id, awaitingCardsMessageId, userHasScrolled, isReplacingStreamingMessage, isNearBottom, autoScrollOnStreamingComplete, scrollToBottom]);


  // 7. Cleanup effect
  useEffect(() => {
    // Capture refs at effect creation time to avoid stale references
    const fallbackTimeoutRef_current = fallbackTimeoutRef.current;
    const scrollLockTimeoutRef_current = scrollLockTimeoutRef.current;
    const welcomeTimeoutRef_current = welcomeTimeoutRef.current;
    const objectiveActionTimeoutRef_current = objectiveActionTimeoutRef.current;
    
    return () => {
      // Clear timeouts on unmount using captured values
      if (fallbackTimeoutRef_current) {
        clearTimeout(fallbackTimeoutRef_current);
      }
      if (scrollLockTimeoutRef_current) {
        clearTimeout(scrollLockTimeoutRef_current);
      }
      if (welcomeTimeoutRef_current) {
        clearTimeout(welcomeTimeoutRef_current);
      }
      if (objectiveActionTimeoutRef_current) {
        clearTimeout(objectiveActionTimeoutRef_current);
      }
    };
  }, []);

  // Client-side chat creation removed; server creates chat on project creation

  const deleteChatMutation = useMutation({
    mutationFn: (chatId) => chatAPI.deleteChat(chatId),
    onSuccess: (_, deletedChatId) => {
      queryClient.invalidateQueries({ queryKey: ['chats', effectiveProjectId] });
      if (deletedChatId === effectiveChatId) {
        navigate(`/projects/${effectiveProjectId}`);
        if (effectiveSetActiveChatId && typeof effectiveSetActiveChatId === 'function') {
          effectiveSetActiveChatId(null);
        }
      }
    }
  });

  // Handlers
  const handleSendMessage = useCallback(async (message, imageUrl = null, extraMetadata = null) => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    // ISSUE 2 FIX: More graceful handling when not connected (e.g., during reconnection)
    if (!effectiveChatId || !isConnected) {
      // Show a friendlier message that indicates reconnection is in progress
      toast.error('Reconnecting... please try again in a moment');
      return;
    }
    let clientMessageId = null;

    try {
      // ISSUE 3 FIX: Auto-dismiss stale interactive cards when a new user message is sent
      // This prevents users from clicking "Accept" on old cards that are no longer relevant
      try {
        const currentMessages = allMessagesRef.current;
        if (currentMessages && messageCardsRef.current) {
          const cardsToAutoDismiss = [];
          
          for (const msg of currentMessages) {
            if (msg.role !== 'assistant' || !msg.id) continue;
            
            // Get cards for this message
            const entry = messageCardsRef.current.get(msg.id);
            const cards = entry ? (Array.isArray(entry.cards) ? entry.cards : (Array.isArray(entry) ? entry : [])) : [];
            if (!cards.length) continue;
            
            // Check which cards are already handled
            const handledCardIds = Array.isArray(msg?.message_metadata?.handled_cards) ? msg.message_metadata.handled_cards : [];
            
            // Find cards that need to be auto-dismissed (plan_update and objective_match types)
            for (const card of cards) {
              if (!card || !card.id) continue;
              
              // Skip if already handled
              if (handledCardIds.includes(card.id)) continue;
              
              // Auto-dismiss plan_update and objective_match cards that have pending actions
              if (card.type === 'plan_update' || card.type === 'objective_match' || card.type === 'multi_objective_match') {
                cardsToAutoDismiss.push({ messageId: msg.id, cardId: card.id });
              }
            }
          }
          
          // Fire-and-forget: mark cards as auto-dismissed without blocking message send
          if (cardsToAutoDismiss.length > 0) {
            console.debug('[ChatSection] Auto-dismissing stale cards on new message:', cardsToAutoDismiss.length);
            
            // ISSUE 3 FIX: Immediately update local state so React re-renders with dismissed cards
            const newAutoDismissedIds = new Set(cardsToAutoDismiss.map(c => c.cardId));
            setChatState(prev => ({
              ...prev,
              autoDismissedCardIds: new Set([...prev.autoDismissedCardIds, ...newAutoDismissedIds])
            }));
            
            // Also update backend (fire-and-forget)
            for (const { messageId, cardId } of cardsToAutoDismiss) {
              // Use silent auto_dismissed state - backend should not trigger system message
              interactiveCardsAPI.markHandled({
                messageId,
                cardId,
                state: { state: 'auto_dismissed', reason: 'new_user_message' }
              }).catch(err => {
                console.debug('[ChatSection] Non-critical: failed to auto-dismiss card', cardId, err);
              });
            }
          }
        }
      } catch (autoDismissError) {
        // Non-critical - don't block message send
        console.debug('[ChatSection] Non-critical auto-dismiss error:', autoDismissError);
      }
      
      // Clear ALL AI pins (temp AND persisted) when a new message is sent
      // BUT: Don't clear pins when selecting a location card (we just set them!)
      const isLocationCardSelection = extraMetadata?.selected_location_card === true;
      try { 
        if (!isLocationCardSelection) {
          onAISuggestionsChange && onAISuggestionsChange([]); 
          onPersistLocationPin && onPersistLocationPin(null); // Clear persisted pins too
        }
      } catch (_) {}
      // Prepare base metadata (must stay serializable; never include File/Blob objects)
      const baseMetadata = { ...(extraMetadata || {}) };

      // Support two image modes:
      // - string URL: already uploaded image URL
      // - object: { file: File|Blob, previewUrl?: string } for optimistic local preview while uploading
      const isImageObject =
        imageUrl &&
        typeof imageUrl === 'object' &&
        (imageUrl.file instanceof File || imageUrl.file instanceof Blob);
      const imageFile = isImageObject ? imageUrl.file : null;
      const providedPreviewUrl = isImageObject && typeof imageUrl.previewUrl === 'string' ? imageUrl.previewUrl : null;
      const localPreviewUrl = imageFile
        ? (providedPreviewUrl || URL.createObjectURL(imageFile))
        : null;

      const optimisticImageUrl =
        (typeof imageUrl === 'string' && imageUrl) ? imageUrl : (localPreviewUrl || null);

      // Metadata used for rendering the optimistic message immediately
      const optimisticMetadata = {
        ...baseMetadata,
        ...(optimisticImageUrl ? { imageUrl: optimisticImageUrl } : {}),
        ...(imageFile ? { _uploading_image: true } : {})
      };

      // Metadata that will actually be sent to the server (no blob URLs)
      const sendMetadata = {
        ...baseMetadata,
        ...(typeof imageUrl === 'string' && imageUrl ? { imageUrl } : {}),
        // Voice mode rides on message metadata (like location): flips the
        // response schema to include speak/ui_action for this turn.
        ...(voiceModeActiveRef.current ? { voice_mode: true } : {})
      };

      // Create client-side correlation ID for optimistic reconciliation
      clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? `c_${crypto.randomUUID()}`
        : `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

      const optimisticBaseMs = Math.max(
        Date.now(),
        lastPersistedServerTimestamp || 0,
        latestChronologicalTimestampRef.current || 0
      );
      const optimisticCreatedAt = new Date(optimisticBaseMs + OPTIMISTIC_TIMESTAMP_BUFFER_MS).toISOString();

      // Create optimistic message to show immediately
      const optimisticMsg = {
        id: `optimistic-${Date.now()}`,
        role: 'user',
        content: message,
        createdAt: optimisticCreatedAt,
        chatId: effectiveChatId,
        isOptimistic: true,
        client_message_id: clientMessageId,
        // UI reads message_metadata in most places; keep both for compatibility
        metadata: optimisticMetadata,
        message_metadata: { ...optimisticMetadata }
      };

      // Show optimistic message immediately
      setOptimisticMessage(optimisticMsg);
      optimisticTimestampMapRef.current.set(clientMessageId, optimisticCreatedAt);
      // console.log('🟢 Optimistic message created:', optimisticMsg.id, 'Content:', optimisticMsg.content);

      // For text-only or already-uploaded images, start generation immediately.
      // For local-file images, wait until upload completes and we actually send the message.
      if (!imageFile) {
        setIsGenerating(true);
      }
      
      // If this is an image message, trigger immediate scroll
      if (optimisticImageUrl && forceScrollToBottom) {
        setTimeout(() => {
          forceScrollToBottom();
        }, 100);
      }

      // If we have a local-file image, upload it now (optimistic preview is already shown).
      if (imageFile) {
        try {
          const uploadResult = await uploadService.uploadImage(imageFile);
          if (!uploadResult?.success) {
            toast.error(`Image upload failed: ${uploadResult?.error || 'Unknown error'}`);
            try {
              if (optimisticImageUrl && optimisticImageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(optimisticImageUrl);
              }
            } catch (_) {}
            optimisticTimestampMapRef.current.delete(clientMessageId);
            setOptimisticMessage(null);
            return;
          }
          const serverUrl = uploadResult?.data?.url || null;
          if (!serverUrl) {
            toast.error('Image upload failed: missing URL');
            try {
              if (optimisticImageUrl && optimisticImageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(optimisticImageUrl);
              }
            } catch (_) {}
            optimisticTimestampMapRef.current.delete(clientMessageId);
            setOptimisticMessage(null);
            return;
          }

          // Update optimistic message to point to the server URL (so viewer uses real URL)
          setOptimisticMessage((current) => {
            if (!current || current.client_message_id !== clientMessageId) return current;
            const nextMeta = {
              ...(current.message_metadata || current.metadata || {}),
              imageUrl: serverUrl,
              _uploading_image: false
            };
            return {
              ...current,
              metadata: { ...nextMeta },
              message_metadata: { ...nextMeta }
            };
          });

          // Populate server metadata with the real URL
          sendMetadata.imageUrl = serverUrl;

          // Now start generation (we've actually sent/are about to send the prompt)
          setIsGenerating(true);

          // Revoke the blob preview after a short delay (allows React to swap src first)
          if (optimisticImageUrl && optimisticImageUrl.startsWith('blob:')) {
            setTimeout(() => {
              try { URL.revokeObjectURL(optimisticImageUrl); } catch (_) {}
            }, 1000);
          }
        } catch (err) {
          console.error('Image upload failed:', err);
          toast.error('Failed to upload image');
          try {
            if (optimisticImageUrl && optimisticImageUrl.startsWith('blob:')) {
              URL.revokeObjectURL(optimisticImageUrl);
            }
          } catch (_) {}
          optimisticTimestampMapRef.current.delete(clientMessageId);
          setOptimisticMessage(null);
          return;
        }
      }

      // Attempt to fetch location without blocking
      const tryGetLocationQuickly = async () => {
        if (!navigator.geolocation || !LocationConsentService.canAccessLocation()) return null;
        try {
          const geoPromise = new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const location = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                // Fix quality travels with the fix: urban-canyon wobble is
                // 50-100 m and the guide must not argue street-level
                // positions on a coarse fix.
                if (typeof pos.coords.accuracy === 'number' && Number.isFinite(pos.coords.accuracy)) {
                  location.accuracy = Math.round(pos.coords.accuracy);
                }
                // Prefer compass heading from Device Orientation API (works even when stationary)
                if (compassHeadingRef.current !== null && typeof compassHeadingRef.current === 'number') {
                  location.heading = compassHeadingRef.current;
                }
                // Fallback to geolocation heading if compass not available (only when device is moving)
                else if (typeof pos.coords.heading === 'number' && !Number.isNaN(pos.coords.heading) && pos.coords.heading >= 0) {
                  location.heading = ((pos.coords.heading % 360) + 360) % 360;
                }
                resolve(location);
              },
              () => resolve(null),
              {
                enableHighAccuracy: false,
                timeout: 5000,
                maximumAge: 300000
              }
            );
          });
          const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve('timeout'), 300));
          const result = await Promise.race([geoPromise, timeoutPromise]);
          if (result && result !== 'timeout') {
            return result;
          }
          return null;
        } catch (_) {
          return null;
        }
      };

      let location = null;
      try {
        location = await tryGetLocationQuickly();
      } catch (_) {}
      // Fallback to last-known userLocation from state (non-blocking)
      if (!location && userLocation && typeof userLocation.lat === 'number' && typeof userLocation.lng === 'number') {
        location = { lat: userLocation.lat, lng: userLocation.lng };
        if (typeof userLocation.accuracy === 'number' && Number.isFinite(userLocation.accuracy)) {
          location.accuracy = Math.round(userLocation.accuracy);
        }
        // Include heading from userLocation if available (prefer compass, fallback to geolocation)
        if (compassHeadingRef.current !== null && typeof compassHeadingRef.current === 'number') {
          location.heading = compassHeadingRef.current;
        } else if (typeof userLocation.heading === 'number' && !Number.isNaN(userLocation.heading)) {
          location.heading = userLocation.heading;
        }
      }
      // If we have location but no heading, try to get compass heading
      if (location && !location.heading && compassHeadingRef.current !== null && typeof compassHeadingRef.current === 'number') {
        location.heading = compassHeadingRef.current;
      }
      if (location) {
        sendMetadata.location = location;
      }

      // Send the message via WebSocket (with whatever metadata we have now)
      const messageData = {
        type: 'chat_message',
        data: {
          content: message,
          chatId: effectiveChatId,
          projectId: effectiveProjectId,
          client_message_id: clientMessageId,
          metadata: sendMetadata
        }
      };
      await sendWebSocketMessage(messageData);

    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      optimisticTimestampMapRef.current.delete(clientMessageId);
      // Clear optimistic message on error
      setOptimisticMessage(null);
    }
  }, [isReadOnly, effectiveChatId, isConnected, sendWebSocketMessage, effectiveProjectId, forceScrollToBottom, userLocation, onAISuggestionsChange, onPersistLocationPin, lastPersistedServerTimestamp]);

  useEffect(() => {
    const pending = location.state?.pendingInitialMessage;
    if (!pending || isReadOnly || !isConnected || !effectiveChatId) {
      return;
    }

    const pendingId = pending.id || `${effectiveChatId}:${pending.content || ''}:${pending.metadata?.imageUrl || ''}`;
    if (pendingInitialMessageSentRef.current === pendingId) {
      return;
    }

    pendingInitialMessageSentRef.current = pendingId;
    const metadata = pending.metadata && typeof pending.metadata === 'object' ? pending.metadata : {};
    const imageUrl = metadata.imageUrl || null;
    handleSendMessage(pending.content || '', imageUrl, metadata);

    const nextState = { ...(location.state || {}) };
    delete nextState.pendingInitialMessage;
    navigate(location.pathname, { replace: true, state: nextState });
  }, [location.state, location.pathname, isReadOnly, isConnected, effectiveChatId, handleSendMessage, navigate]);

  // Retry handler for error assistant messages – resend the last user message before the error
  const handleRetryMessage = useCallback((errorMessage) => {
    try {
      if (!errorMessage || !Array.isArray(allMessages) || allMessages.length === 0) {
        return;
      }

      // Find the index of the error message in the unified message list
      const idx = allMessages.findIndex(
        (m) => (m?.id && errorMessage.id && m.id === errorMessage.id)
      );

      let userMessage = null;

      if (idx !== -1) {
        // Walk backwards to find the preceding user message
        for (let i = idx - 1; i >= 0; i -= 1) {
          const candidate = allMessages[i];
          if (candidate && candidate.role === 'user') {
            userMessage = candidate;
            break;
          }
        }
      }

      // Fallback: if we couldn't locate by index, use the most recent user message
      if (!userMessage) {
        for (let i = allMessages.length - 1; i >= 0; i -= 1) {
          const candidate = allMessages[i];
          if (candidate && candidate.role === 'user') {
            userMessage = candidate;
            break;
          }
        }
      }

      if (!userMessage) {
        toast.error('Could not find a message to retry.');
        return;
      }

      const baseContent = userMessage.content || '';
      const meta = userMessage.message_metadata || userMessage.metadata || {};
      const imageUrl = meta.imageUrl || null;

      if (!baseContent && !imageUrl) {
        toast.error('Original message had no content to retry.');
        return;
      }

      handleSendMessage(baseContent, imageUrl, meta);
    } catch (e) {
      console.error('Failed to retry message:', e);
      toast.error('Failed to retry message');
    }
  }, [allMessages, handleSendMessage]);

  const handleInsufficientTokensTopUp = useCallback((message) => {
    const metadata = message?.message_metadata || message?.metadata || {};
    const billing = metadata?.billing || {};

    openPaywall({
      isGated: false,
      tokensRequired:
        billing.tokensRequired !== undefined ? billing.tokensRequired : null,
      tokensRemaining:
        billing.tokensRemaining !== undefined ? billing.tokensRemaining : tokenBalance,
      source: 'chat_history',
    });
  }, [openPaywall, tokenBalance]);

  // Handle card clicks - send the card's action as a message
  const handleCardClick = useCallback((action, card) => {
    try {
      // Disable all interactions in read-only mode
      if (isReadOnly) {
        return;
      }
      
      // Validate card before processing
      if (!card || !card.id) {
        console.error('Invalid card clicked:', card);
        return;
      }

      // Wrap-up decision option flagged `action: "end_quest"`: formally end the
      // session (archive → project page → review modal) instead of letting the
      // AI improvise a goodbye that ends nothing.
      if (card?.decision_data?.action === 'end_quest') {
        if (typeof onRequestFinishProject === 'function') {
          try { onRequestFinishProject({ skipConfirmation: true }); } catch (_) {}
          return;
        }
      }
    // Handle objective match confirm/dismiss inline actions
    if (action && typeof action === 'object') {
      const act = action; // action payload from objective_match
      const type = act?.type;
      const matchId = act?.matchId;
      if ((type === 'confirm_match' || type === 'dismiss_match') && matchId) {
        // Use already-imported APIs directly (no dynamic import needed)
        const confirmOrDismiss = async () => {
          try {
            // Show global spinner: confirm/dismiss triggers an agent follow-up (server scheduled).
            setIsGenerating(true);
            if (objectiveActionTimeoutRef.current) {
              clearTimeout(objectiveActionTimeoutRef.current);
            }
            objectiveActionTimeoutRef.current = setTimeout(() => {
              setIsGenerating(false);
              objectiveActionTimeoutRef.current = null;
            }, 20000);

            // Call the confirm/dismiss API. The visitor's live voice-mode
            // state rides along so the system-triggered follow-up turn can
            // include the `speak` field and be narrated like a normal reply.
            const matchPayload = voiceModeActiveRef.current
              ? { voice_mode: true }
              : undefined;
            const apiCall = type === 'confirm_match'
              ? objectiveMatchAPI.confirm(matchId, matchPayload)
              : objectiveMatchAPI.dismiss(matchId, matchPayload);
            await apiCall;
            
            // Invalidate and refetch project query to update objectives list
            if (effectiveProjectId && queryClient) {
              console.log('🎯 Objective match confirmed/dismissed, invalidating project query:', effectiveProjectId);
              await queryClient.invalidateQueries({ queryKey: createQueryKey.project(effectiveProjectId) });
              // Refetch immediately to ensure fresh data
              await queryClient.refetchQueries({ queryKey: createQueryKey.project(effectiveProjectId) });
              // Also invalidate the generic projects list
              await queryClient.invalidateQueries({ queryKey: ['projects'] });
            }
            
            // Refetch objective match statuses to update UI immediately
            const idsToFetch = [...new Set([...allObjectiveMatchIds, matchId])];
            if (idsToFetch.length > 0) {
              try {
                const res = await objectiveMatchAPI.getStatuses(idsToFetch);
                const data = res?.data?.data || {};
                setObjectiveMatchStatuses(data);
              } catch (statusErr) {
                console.warn('Failed to fetch match statuses:', statusErr);
              }
            }
            
            // Callback for celebration/UI updates
            if (type === 'confirm_match' && typeof onObjectiveConfirmed === 'function') {
              try { onObjectiveConfirmed(); } catch (_) {}
            }
          } catch (err) {
            console.error('Failed to confirm/dismiss objective match:', err);
            setIsGenerating(false);
            if (objectiveActionTimeoutRef.current) {
              clearTimeout(objectiveActionTimeoutRef.current);
              objectiveActionTimeoutRef.current = null;
            }
          }
        };
        
        confirmOrDismiss();
        return;
      }
      if (type === 'end_quest') {
        if (typeof onRequestFinishProject === 'function') {
          try { onRequestFinishProject(); } catch (_) {}
        } else {
          handleSendMessage("Yes, I'm done. Please finish the project now.");
        }
        return;
      }
      if (act?.action) {
        handleSendMessage(act.action);
      }
      return;
    }

    // console.log('🎴 Card clicked:', card.title, card.type === 'location' ? '(location pin)' : `→ ${action}`);
    // For empty actions, provide sensible defaults instead of no-op
    if (!action || (typeof action === 'string' && action.trim().length === 0)) {
      if (card && card.type === 'location' && card.locations && card.locations.length > 0) {
        // Persist the first location as a non-temporary pin
        const location = card.locations[0];
        // console.log('📍 Persisting location pin:', location.name, 'at', location.location);
        try {
          if (typeof onPersistLocationPin === 'function' && location.location) {
            const lat = Number(location.location.lat);
            const lng = Number(location.location.lng);
            const persistedPin = {
              id: `ai-persist-${location.id || 'pin'}-${lat.toFixed(6)}-${lng.toFixed(6)}`,
              name: location.name || 'Location',
              description: location.description || '',
              coordinates: { lat, lng },
              confidence: 1.0
            };
            // console.log('📍 Persisted pin object:', persistedPin);
            onPersistLocationPin(persistedPin);
          }
        } catch (e) {
          console.error('Failed to persist location pin:', e);
        }

        // Auto-send a brief follow-up asking for details about this place
        const tellMoreText = location?.name
          ? `Tell me more about ${location.name}`
          : 'Tell me more about this place';
        const autoMeta = {
          selected_location_card: true,
          location_card_id: location.id,
          location_card_title: location.name,
          location_card_coordinates: location.location || null
        };
        handleSendMessage(tellMoreText, null, autoMeta);
        return;
      }
      // For non-location cards with no action, default to title or input_template
      action = (card?.input_template && card.input_template.trim()) || (card?.title && `Let's ${card.title}`) || 'Let’s do this step';
    }
    
    // 🎯 CONTINUITY FIX: Add metadata to indicate this was a card selection
    const cardMetadata = {};
    
    // 🎯 Guide card handling: extract guide_data for objective completion tracking
    // This MUST be checked first because guide_data may be present on any card type
    if (card?.guide_data) {
      cardMetadata.guide_data = card.guide_data;
      // Also include objective linkage if present
      if (card.project_objective_id) {
        cardMetadata.project_objective_id = card.project_objective_id;
      }
      if (card.title) {
        cardMetadata.title = card.title;
      }
    }
    
    if (card && card.type === 'location') {
      // Explore card selected - user wants to see location
      cardMetadata.selected_location_card = true;
      cardMetadata.location_card_id = card.id;
      cardMetadata.location_card_title = card.title;
      if (card.location) {
        cardMetadata.location_card_coordinates = card.location;
      }
      // console.log('📍 User selected explore card:', card.title);
    } else if (
      card &&
      (card.type === 'observe_photo' ||
        card.type === 'observe_form' ||
        card.type === 'observe' || // legacy
        card.type === 'learn')
    ) {
      // Action card selected - user wants to do this
      cardMetadata.selected_action_card = true;
      cardMetadata.action_card_id = card.id;
      cardMetadata.action_card_title = card.title;
      cardMetadata.action_card_type = card.type;
      // Include full payload for backend to use
      cardMetadata.action_card_payload = card;
      // Explicitly include objective linkage for easier backend access
      if (card.project_objective_id) {
        cardMetadata.project_objective_id = card.project_objective_id;
      }
      // Include structured data if present (from form submission)
      if (card.structured_data) {
        cardMetadata.structured_data = card.structured_data;
      }
      // console.log('🎯 User selected action card:', card.title);
    } else if (card && card.type === 'guide') {
      // Guide card interaction (done/skipped/option selected)
      cardMetadata.guide_card_id = card.id;
      cardMetadata.guide_card_title = card.title;
      cardMetadata.guide_card_type = card.guide_intent;
      // guide_data already handled above
    }
    
    // For explore cards, send a specific request for multiple locations
    const outgoing = (card && card.type === 'location')
      ? `Show me multiple nearby locations for ${card?.title || 'exploration'}. Generate 3-5 different places I can visit.`
      : action;
    // Best-practice behavior: no card.intent. Use type + payload.
    const isObserveForm =
      card?.type === 'observe_form' ||
      (card?.type === 'observe' && card?.form_fields && card.form_fields.length > 0);
    const isObservePhoto =
      card?.type === 'observe_photo' ||
      (card?.type === 'observe' && !(card?.form_fields && card.form_fields.length > 0));

    // Structured data form submission - send message with structured data (check FIRST to prevent photo flow)
    const hasStructuredData = !!(card?.structured_data && isObserveForm);
    // Don't trigger photo flow for structured data cards (they show forms instead)
    const wantsPhoto = !!isObservePhoto;
    const wantsLearnMore = card?.type === 'learn';
    // text flow: prefill composer when evidence requests text/description/note
    const wantsText = !!(
      card?.evidence &&
      (card.evidence.type === 'text' ||
        card.evidence.type === 'description' ||
        card.evidence.type === 'note')
    );
    
    // Handle structured data form submission
    if (hasStructuredData) {
      const formMessage = card.structured_data?.form_card_id 
        ? `Completed ${card.title || 'form'}`
        : card.title 
          ? `Completed ${card.title}`
          : 'Form submitted';
      
      // Extract photo URLs from structured data form submission for vision analysis
      // Photos in structured data forms should be analyzed the same way as regular observer card photos
      let formImageUrl = null;
      if (card.structured_data?.fields && card.structured_data?.values) {
        // Find photo fields from the fields array
        const photoFieldIds = card.structured_data.fields
          .filter(field => field.type === 'photo' && field.value)
          .map(field => field.id);
        
        // Extract photo URLs from values using photo field IDs
        for (const fieldId of photoFieldIds) {
          const photoUrl = card.structured_data.values[fieldId];
          if (photoUrl && typeof photoUrl === 'string' && photoUrl.trim()) {
            formImageUrl = photoUrl; // Use first photo for vision analysis
            break; // Analyze the first photo (backend can be extended to handle multiple photos later)
          }
        }
      }
      
      handleSendMessage(formMessage, formImageUrl, cardMetadata);
      return;
    }
    if (wantsPhoto) {
      const overlayHint = card?.instruction_image_url || card?.overlay_hint || null;
      try {
        // Don't auto-send: let user add notes before submitting
        // Photo will appear in composer where user can add context/notes
        setExternalCommand({ 
          type: 'open_camera', 
          prefillText: '', 
          autoSend: false, 
          overlayHint, 
          metadata: cardMetadata,
          // Provide context for the composer UI
          cardTitle: card?.title,
          cardType: 'observe_photo'
        });
      } catch (_) {}
      return;
    }
    if (wantsLearnMore) {
      const learnMorePrompt = `Tell me comprehensive details about ${card?.title || 'this topic'}`;
      // Auto-submit the learn more request
      handleSendMessage(learnMorePrompt, null, { ...cardMetadata, learn_more: true });
      return;
    }
    if (wantsText) {
      const prefillText = card?.input_template || `${card?.title || 'Observation'}: `;
      try {
        setExternalCommand({ type: 'prefill_text', prefillText, autoSend: false, metadata: cardMetadata });
      } catch (_) {}
      return;
    }
    handleSendMessage(outgoing, null, cardMetadata);
    
    // Force scroll to bottom after sending the message (bypasses user scroll state)
    setTimeout(() => {
      if (forceScrollToBottom) {
        forceScrollToBottom();
      }
    }, 150);
    } catch (error) {
      console.error('Error handling card click:', error);
      // Show user-friendly error message
      if (typeof handleSendMessage === 'function') {
        handleSendMessage("Sorry, there was an error processing that action. Please try again.", null, { error: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly, handleSendMessage, forceScrollToBottom, effectiveProjectId, onRequestFinishProject, onObjectiveConfirmed, onPersistLocationPin, effectiveProject, queryClient, allObjectiveMatchIds, setObjectiveMatchStatuses]);

  // Handle spotlight "Learn More" clicks
  const handleLearnMore = useCallback((learnMorePrompt, spotlight) => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    // console.log('🔍 Learn more clicked for:', spotlight.entity, '→', learnMorePrompt);
    
    // 🎯 CONTINUITY FIX: Add metadata to indicate this is a learn-more request
    const learnMoreMetadata = {
      learn_more: true,
      spotlight: spotlight,
      spotlight_entity: spotlight?.entity || spotlight?.title
    };
    
    handleSendMessage(learnMorePrompt, null, learnMoreMetadata);
    
    // Force scroll to bottom after sending the message (bypasses user scroll state)
    setTimeout(() => {
      if (forceScrollToBottom) {
        forceScrollToBottom();
      }
    }, 150);
  }, [isReadOnly, handleSendMessage, forceScrollToBottom]);

  const handleStopGeneration = useCallback(() => {
    // Disable all interactions in read-only mode
    if (isReadOnly) {
      return;
    }
    
    if (effectiveChatId && isConnected) {
      sendWebSocketMessage({
        type: 'stop_generation',
        chatId: effectiveChatId,
        data: { chatId: effectiveChatId }
      });
    }
    setIsGenerating(false);
  }, [isReadOnly, effectiveChatId, isConnected, sendWebSocketMessage]);

  // Auto-select first chat if none is active (server creates chat)
  useEffect(() => {
    if (!isReadOnly && !effectiveChatId && Array.isArray(chatsData) && chatsData.length > 0) {
      const firstChat = chatsData[0];
      if (firstChat?.id && effectiveSetActiveChatId) {
        effectiveSetActiveChatId(firstChat.id);
      }
    }
  }, [isReadOnly, effectiveChatId, chatsData, effectiveSetActiveChatId]);

  // Clear interactive cards and cropped images when switching chats
  // Only clear when chatId actually changes from one valid ID to another
  const prevChatIdRef = useRef(effectiveChatId);
  useEffect(() => {
    const prevId = prevChatIdRef.current;
    const currentId = effectiveChatId;
    
    // Only clear cards when switching between two valid chat IDs
    // Don't clear on initial load (null -> valid ID) or when going to null
    if (prevId && currentId && prevId !== currentId) {
      console.debug('[cards] chat id changed, clearing cards', { from: prevId, to: currentId });
      setChatState(prev => ({
        ...prev,
        messageCards: new Map(),
        croppedImages: new Map(),
        autoDismissedCardIds: new Set() // ISSUE 3 FIX: Clear auto-dismissed state when switching chats
      }));
    }
    
    prevChatIdRef.current = currentId;
  }, [effectiveChatId]);

  // Client auto-welcome disabled; server sends welcome on project start

  const handleChatSelect = useCallback((chatId) => {
    navigate(`/projects/${effectiveProjectId}/chats/${chatId}`);
    effectiveSetActiveChatId(chatId);
    setActiveTab('chat');
    resetScrollState();
  }, [effectiveProjectId, navigate, effectiveSetActiveChatId, resetScrollState]);

  const handleDeleteChat = useCallback((chatId) => {
    // Use window.confirm to avoid ESLint error, or implement a custom modal later
    if (window.confirm('Delete this nature conversation?')) {
      deleteChatMutation.mutate(chatId);
    }
  }, [deleteChatMutation]);

  // Do not block rendering while chats load; allow the main UI to render immediately

  // (Moved read-only empty state into render to keep hooks top-level)

  // Debug logging
  // console.log('ChatSection render debug:', {
  //   effectiveChatId,
  //   chatId,
  //   activeChatId,
  //   activeTab,
  //   isConnected,
  //   allMessagesLength: allMessages?.length || 0,
  //   hasAllMessages: !!allMessages
  // });

  // Show loader when text is streaming but cards for that streaming message haven't arrived yet
  const isAwaitingCards = useMemo(() => {
    // Helper to check if message has cards in metadata (for saved messages)
    // This prevents spinner from persisting if cards are already in the message history but not in messageCards Map
    const hasCardsInMetadata = (messageId) => {
        if (!allMessages || !Array.isArray(allMessages)) return false;
        const msg = allMessages.find(m => m.id === messageId);
        const cards = msg?.message_metadata?.interactive_cards;
        // IMPORTANT: empty [] means "final with no cards" (e.g., chat_only) and should clear the spinner.
        return Boolean(Array.isArray(cards));
    };

    // Spinner during streaming while cards not yet present
    const duringStreaming = Boolean(
      isGenerating &&
      streamingMessage?.id &&
      streamingMessage?.content &&
      !messageCards.has(streamingMessage.id)
    );
    // Spinner after streaming completes while waiting for cards for the saved AI message id
    const afterStreaming = Boolean(
      awaitingCardsMessageId && 
      !messageCards.has(awaitingCardsMessageId) &&
      !hasCardsInMetadata(awaitingCardsMessageId)
    );
    return duringStreaming || afterStreaming;
  }, [isGenerating, streamingMessage?.id, streamingMessage?.content, messageCards, awaitingCardsMessageId, allMessages]);

  // Clear awaiting-cards state once cards for that message are present
  useEffect(() => {
    if (awaitingCardsMessageId && messageCards.has(awaitingCardsMessageId)) {
      setAwaitingCardsMessageId(null);
    }
  }, [awaitingCardsMessageId, messageCards]);

  return (
    // data-chat-root scopes the press-and-hold-to-talk gesture: long-press on
    // any non-interactive surface inside this container starts a walkie
    // recording (voice mode only) — modals and sidebars stay out of it.
    <div className="flex flex-col h-full" data-chat-root>
      {/* Header */}
      {/* <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-sm font-medium text-gray-700">{projectResponse?.data?.name || 'OKIO'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('chat')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                activeTab === 'chat' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <MessageCircle size={12} className="inline mr-1" />
              Chat
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                activeTab === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <History size={12} className="inline mr-1" />
              History
            </button>
          </div>
          <button
            onClick={handleNewChat}
            disabled={createChatMutation.isPending}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="New nature conversation"
          >
            <Plus size={16} />
          </button>
        </div>
      </div> */}

      {/* Content */}
      {activeTab === 'chat' ? (
        <div className="flex flex-col h-full">
          
          
          {/* Messages Container */}
          <div 
            ref={containerRef}
            className="flex-1 overflow-y-auto mobile-scroll overscroll-contain"
            onScroll={handleScroll}
          >
            {/* Load More Button - Hidden in read-only mode */}
            {hasMore && !isReadOnly && (
              <div className="flex justify-center p-4">
                <button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="animate-spin" size={24} />
                    </>
                  ) : (
                    <>
                      <ChevronUp size={14} />
                      Load earlier messages
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Messages */}
            <div className="px-4 pt-0 pb-4 md:pt-8 md:pb-8 2xl:px-0 max-w-3xl mx-auto">
              {/* Project Header - On-brand design with decorative icon */}
              {effectiveProject?.name && (
                <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
                 
                  {/* Main content - flex layout with icon */}
                  <div className="relative flex">
                    {/* Decorative icon section - matching InteractiveCards style */}
                    <div className="relative w-16 sm:w-20 min-h-full overflow-hidden flex-shrink-0">
                      <div className="absolute inset-0 w-full h-full bg-emerald-100 dark:bg-emerald-400/20 flex items-center justify-center overflow-hidden">
                        {/* Pattern overlay */}
                        <div className="absolute inset-0 w-full h-full">
                          <img
                            src="/svg/pattern1.svg"
                            alt=""
                            className="w-full h-full object-cover opacity-[0.04] dark:opacity-[0.1]"
                            style={{ filter: 'brightness(0) saturate(100%)' }}
                          />
                        </div>
                        {/* Animated eyes icon */}
                        <div className="relative z-10">
                          <LoadingIndicator className="!mt-0 !py-0 !px-0" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Content section */}
                    <div className="flex-1 p-4 sm:p-5 min-w-0">
                      {/* Title row */}
                      <div className="flex items-start justify-between gap-3">
                        <h1 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white leading-tight" aria-label={`Project: ${effectiveProject.name}`}>
                          {effectiveProject.name}
                        </h1>
                      </div>

                      {/* Description - truncated with expand */}
                      {effectiveProject?.description && (
                        <TruncatedDescription text={effectiveProject.description} />
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              
              {/* ✅ SIMPLIFIED: Show messages area as soon as we have any content OR are generating */}
              {isReadOnly && !effectiveChatId ? (
                <div className="text-center py-12">
                  <div className="text-lg font-medium text-gray-900 dark:text-white mb-2">No chat selected</div>
                  <div className="text-sm text-gray-600">This adventure doesn't have a chat history.</div>
                </div>
              ) : (
                <>
                  {allMessages?.length > 0 || isGenerating ? (
                  <div className="pb-20">
                    {/* Voice how-to badge: the one-time education for the
                        press-and-hold-to-talk gesture. Chat-native (scrolls
                        away as the session grows), voice-mode sessions only,
                        rendered as a system element — not a guide message. */}
                    {voiceModeActive && voiceSettingsReady && (() => {
                      const de = (i18n?.language || '').toLowerCase().startsWith('de');
                      // The whole chat surface (cards included) arms the
                      // hold-to-talk gesture, so holding the badge itself
                      // starts a recording — the instruction is its own demo.
                      return (
                        <div className="mt-4 mb-4 flex items-center rounded-2xl overflow-hidden border border-emerald-400/20 shadow">
                          <img
                            src="/home/voice-hold-to-talk.webp"
                            alt=""
                            className="w-28 self-stretch object-cover object-left shrink-0"
                            loading="lazy"
                          />
                          <div className="px-4 py-3 min-w-0">
                            <p className="text-sm font-semibold text-white leading-snug">
                              {de
                                ? 'Sprich einfach mit deinem Guide'
                                : 'Just talk to your guide'}
                            </p>
                            <p className="text-xs text-white/50 mt-0.5 leading-snug">
                              {de
                                ? 'Bildschirm gedrückt halten, sprechen, loslassen.'
                                : 'Press and hold the screen, speak, let go.'}
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                    {allMessages?.map((message) => {
                      // Guide-card protocol strings → compact status chip
                      const guideMarker = getGuideMarker(message);
                      if (guideMarker) {
                        return (
                          <GuideMarkerChip
                            key={message.client_message_id || message.id}
                            marker={guideMarker}
                          />
                        );
                      }
                      return (
                      <div key={message.client_message_id || message.id} className={message.role === 'user' ? 'card-base-user' : 'card-base-agent'}>
                        <StreamingMessage
                          message={message}
                          onImageClick={openViewerForMessage}
                          onRetry={handleRetryMessage}
                          tokenBalance={tokenBalance}
                          onInsufficientTokensContinue={handleRetryMessage}
                          onInsufficientTokensTopUp={handleInsufficientTokensTopUp}
                          voicePlayback={voicePlaybackCtx}
                        />
                        {/* Tour contract: deterministic route shape under the opening
                            message — what a human guide states up front ("8 stops,
                            about 90 minutes"). Never depends on the model mentioning it. */}
                        {message.role === 'assistant' &&
                          message.id === firstAssistantMessageId &&
                          tourShape && (() => {
                            const de = (i18n?.language || '').toLowerCase().startsWith('de');
                            const kmStr =
                              tourShape.walkKm < 1
                                ? `~${Math.max(50, Math.round((tourShape.walkKm * 1000) / 50) * 50)} m`
                                : de
                                  ? `~${tourShape.walkKm.toFixed(1).replace('.', ',')} km`
                                  : `~${tourShape.walkKm.toFixed(1)} km`;
                            const inner = (
                              <>
                                <span className="flex-shrink-0 w-16 flex items-center justify-center">
                                  <span className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex items-center justify-center ring-4 ring-gray-50 dark:ring-gray-800/40">
                                    <Route className="w-4 h-4" />
                                  </span>
                                </span>
                                <span className="min-w-0 flex-1 pl-4 sm:pl-5">
                                  <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                    {de ? 'Deine Tour' : 'Your tour'}
                                  </span>
                                  <span className="block text-sm font-bold text-gray-900 dark:text-white">
                                    {tourShape.stops} {de ? 'Stationen' : 'stops'} · {kmStr} ·{' '}
                                    ~{tourShape.mins} {de ? 'Min.' : 'min'}
                                  </span>
                                </span>
                              </>
                            );
                            return typeof onOpenGoalsModal === 'function' ? (
                              <button
                                type="button"
                                onClick={() => { try { onOpenGoalsModal(); } catch (_) {} }}
                                className="mt-3 w-full flex items-center py-1.5 rounded-xl text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                              >
                                {inner}
                                <span className="flex-shrink-0 mr-2 inline-flex items-center gap-0.5 px-2.5 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-500 dark:text-gray-400">
                                  {de ? 'Stationen' : 'Stops'}
                                  <ChevronRight className="w-3.5 h-3.5" />
                                </span>
                              </button>
                            ) : (
                              <div className="mt-3 w-full flex items-center py-1.5">{inner}</div>
                            );
                          })()}
                        {/* Map-first handoff: "Go to" journey node — step zero of the turn.
                            Transit-app grammar: round node + dashed leg = travel, rectangular
                            card = task. The dashed connector descends into the action card so
                            the causality (walk there, THEN do) is visible. Same deterministic
                            rule as the map's pulsing pin. */}
                        {stationChipInfo?.messageId === message.id && (() => {
                          const de = (i18n?.language || '').toLowerCase().startsWith('de');
                          // Walking hint: metres/km when plausibly walkable; beyond that
                          // the visitor is off-site, so frame it as distance, not a walk.
                          // Within ~200 m (same radius the agent uses for "arrived") the
                          // row flips from directive (Go to) to confirmatory (You're here).
                          let legLabel = de ? 'zu Fuß' : 'on foot';
                          let arrived = false;
                          if (
                            userLocation &&
                            Number.isFinite(userLocation.lat) &&
                            Number.isFinite(stationChipInfo.lat)
                          ) {
                            const km = haversineKm(
                              userLocation.lat, userLocation.lng,
                              stationChipInfo.lat, stationChipInfo.lng
                            );
                            if (Number.isFinite(km)) {
                              if (km <= 0.2) {
                                arrived = true;
                              } else if (km < 0.95) {
                                const m = Math.max(10, Math.round((km * 1000) / 10) * 10);
                                legLabel = de ? `~${m} m zu Fuß` : `~${m} m walk`;
                              } else if (km < 5) {
                                const kmStr = de ? km.toFixed(1).replace('.', ',') : km.toFixed(1);
                                legLabel = de ? `~${kmStr} km zu Fuß` : `~${kmStr} km walk`;
                              } else {
                                legLabel = de
                                  ? `~${Math.round(km)} km entfernt`
                                  : `~${Math.round(km)} km away`;
                              }
                            }
                          }
                          const kickerLabel = arrived
                            ? (de ? 'Du bist hier' : "You're here")
                            : (de ? 'Gehe zu' : 'Go to');
                          return (
                            <div className="mt-6">
                              <button
                                type="button"
                                onClick={() => {
                                  try { onShowStationOnMap(stationChipInfo.objectiveId); } catch (_) {}
                                }}
                                className="group w-full flex items-center py-1.5 rounded-xl text-left
                                  hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                              >
                                {/* Node slot mirrors the cards' w-16 icon column so the pin
                                    sits on the same vertical axis as the card icons */}
                                <span className="flex-shrink-0 w-16 flex items-center justify-center">
                                  <span className="w-9 h-9 rounded-full bg-indigo-500 text-white flex items-center justify-center shadow-sm ring-4 ring-indigo-100 dark:ring-indigo-500/20">
                                    <MapPin className="w-4 h-4" />
                                  </span>
                                </span>
                                {/* pl matches the cards' content padding (p-4 sm:px-5) */}
                                <span className="min-w-0 flex-1 pl-4 sm:pl-5">
                                  <span className="block text-[11px] font-semibold uppercase tracking-wide text-indigo-500 dark:text-indigo-400">
                                    {kickerLabel} · {de ? 'Station' : 'Stop'} {stationChipInfo.number}
                                  </span>
                                  <span className="block text-sm font-bold text-gray-900 dark:text-white truncate">
                                    {stationChipInfo.name}
                                  </span>
                                </span>
                                <span className="flex-shrink-0 mr-2 inline-flex items-center gap-0.5 px-2.5 py-1.5 rounded-full
                                  border border-indigo-200 dark:border-indigo-500/40
                                  bg-indigo-50 dark:bg-indigo-500/10
                                  text-xs font-semibold text-indigo-600 dark:text-indigo-300
                                  group-hover:bg-indigo-100 dark:group-hover:bg-indigo-500/20 transition-colors"
                                >
                                  {de ? 'Karte' : 'Map'}
                                  <ChevronRight className="w-3.5 h-3.5" />
                                </span>
                              </button>
                              {/* Dashed walking leg descending into the action card below,
                                  on the same 32px axis as the node and card icons.
                                  When the visitor has arrived there is no walk — no leg. */}
                              {!arrived && (
                                <div className="flex items-center gap-2 ml-[31px] -mb-1" aria-hidden="true">
                                  <span className="self-stretch min-h-[26px] border-l-2 border-dashed border-indigo-300 dark:border-indigo-500/40" />
                                  <span className="inline-flex items-center gap-1 py-1 ml-11 text-[11px] font-semibold text-indigo-400 dark:text-indigo-400/80">
                                    <Footprints className="w-3.5 h-3.5" />
                                    {legLabel}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {/* Show all cards for assistant messages (fallback to persisted metadata) */}
                        {message.role === 'assistant' && (() => {
                          const persisted = (message?.message_metadata?.interactive_cards || []);
                          const entryCards = extractCardsFromEntry(messageCards.get(message.id));
                          const cardsToRender = entryCards.length > 0
                            ? entryCards
                            : (Array.isArray(persisted) ? persisted : []);
                          if (!Array.isArray(cardsToRender) || cardsToRender.length === 0) {
                            return false;
                          }
                          return (
                            <CardErrorBoundary>
                              <InteractiveCards
                                cards={[...cardsToRender]}
                                onCardClick={handleCardClick}
                                messageId={message.id}
                                chatIdForStreaming={effectiveChatId}
                                registerHandler={registerHandler}
                                handledCardIds={Array.isArray(message?.message_metadata?.handled_cards) ? message.message_metadata.handled_cards : []}
                                autoDismissedCardIds={autoDismissedCardIds}
                                cardStates={message?.message_metadata?.card_states || {}}
                                croppedImages={croppedImages.get(message.id) || {}}
                                sourceImageUrl={getSourceImageForAssistantMessage(message.id)}
                                assistantMessageId={message.id}
                                onInteractiveCardInteraction={onInteractiveCardInteraction}
                                onLocationCardExpanded={handleLocationCardExpanded}
                                onLocationCardCollapsed={handleLocationCardCollapsed}
                                userLocation={userLocation}
                                allMessages={allMessages}
                                objectiveMatchStatuses={objectiveMatchStatuses}
                                onPlanUpdateComplete={handlePlanUpdateComplete}
                                onOpenSpotlightViewer={({ spotlight, assistantMessageId, sourceImageUrl }) => {
                                  // Open viewer for this assistant message with highlighted spotlight
                                  const cards = extractCardsFromEntry(messageCards.get(assistantMessageId));
                                  const spotlights = cards.filter(c => c.type === 'spotlight');
                                  const imageUrl = sourceImageUrl || (allMessages?.find(m => {
                                    const meta = m?.message_metadata || {};
                                    return m.role === 'assistant' && m.id === assistantMessageId && meta.source_image_url;
                                  })?.message_metadata?.source_image_url) || (allMessages?.find(m => m.role === 'user' && m?.message_metadata?.imageUrl)?.message_metadata?.imageUrl) || null;
                                  if (!imageUrl) return;
                                  setViewerImageUrl(imageUrl);
                                  setViewerSpotlights(spotlights);
                                  setViewerOpen(true);
                                  setHighlightedSpotlightId(spotlight?.id || null);
                                }}
                                onLearnMore={handleLearnMore}
                                isReadOnly={isReadOnly}
                                projectId={projectId}
                                onOpenGoalsModal={onOpenGoalsModal}
                                objectivesSummary={objectivesSummary}
                                autoInstructionImages={effectiveProject?.settings?.auto_instruction_images === true}
                                voicePlayback={voicePlaybackCtx}
                                externalExpandRequest={externalExpandRequest}
                                isLatestMessage={message.id === latestAssistantMessageId}
                              />
                            </CardErrorBoundary>
                          );
                        })()}
                      </div>
                      );
                    })}

                    {/* Show loading indicator while uploading image or waiting for assistant */}
                    {((isGenerating && !streamingMessage?.content) || 
                      (optimisticMessage?.message_metadata?._uploading_image)) && (
                      <div className="flex items-center justify-start mb-4">
                        <LoadingIndicator />
                      </div>
                    )}

                    {/* While waiting for cards to be generated for the current streaming message, show loader again */}
                    {isAwaitingCards && (
                      <div className="flex items-center justify-start mb-4">
                        <LoadingIndicator />
                      </div>
                    )}
                    
                  </div>
              ) : isMessagesLoading || !effectiveProject ? (
                <div className="flex items-center justify-center p-6">
                  <Loader2 className="animate-spin text-gray-400" size={20} />
                </div>
              ) : isWaitingForWelcome && !isReadOnly ? (
                <div className="flex flex-col items-center justify-center mt-4 p-6 text-center">
                  <div className="mb-4">
                    <Loader2 className="animate-spin text-green-500" size={24} />
                  </div>
                  <div className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Preparing your project...
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Setting up your project
                  </div>
                </div>
              ) : welcomeTimeout && !isReadOnly ? (
                <div className="flex flex-col items-center justify-center p-6 text-center">
                  <div className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Welcome to your project!
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Ready to explore and discover?
                  </div>
                  <button
                    onClick={retryWelcomeMessage}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    Start Exploring
                  </button>
                </div>
              ) : effectiveProject?.status === 'ongoing' && !isReadOnly ? (
                <div className="flex items-center justify-start mt-4">
                  <LoadingIndicator 
                    className="px-4 bg-gray-50 dark:bg-gray-800" 
                    showText={true} 
                    text="Preparing your project..." 
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-sm text-gray-500 dark:text-gray-400">No messages yet.</div>
                </div>
              )}
                </>
              )}
            </div>
            
            <div ref={endRef} />
          </div>

          {/* Read-only banner - Sticky at bottom, show when viewing archived/read-only chats */}
          {effectiveChatId && isReadOnly && (
            <div className="sticky bottom-0 z-10 p-3 w-full bg-gray-100 dark:bg-gray-700 shadow-sm rounded-t-2xl">
              <div className="flex items-center justify-between gap-3 max-w-3xl mx-auto">
                <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200 uppercase min-w-0">
                  <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-xs font-medium leading-relaxed truncate">
                    <span className='font-bold mr-1.5'>Read-only:</span>
                    {effectiveProject?.name}
                  </span>
                </div>
                <button
                  onClick={() => navigate(`/projects/${effectiveProjectId}?overview=true`)}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Exit
                </button>
              </div>
            </div>
          )}

          {/* Chat Input - Hidden in read-only mode - Fixed at bottom */}
          {effectiveChatId && !isReadOnly && (
            <IsolatedChatInput
              onSendMessage={handleSendMessage}
              isConnected={isConnected}
              isGenerating={isGenerating}
              onStopGeneration={handleStopGeneration}
              projectId={effectiveProjectId}
              onSubmittedWithImage={onSubmittedWithImage}
              externalCommand={externalCommand}
              onExternalCommandHandled={() => setExternalCommand(null)}
              isReadOnly={isReadOnly}
              voiceAvailable={voiceAvailable}
              voiceReady={voiceSettingsReady}
              voiceMode={voiceModeActive}
              onToggleVoiceMode={toggleVoiceMode}
            />
          )}
          
        </div>
      ) : (
        /* History Tab */
        <div className="flex-1 overflow-y-auto mobile-scroll">
          <ChatHistoryList
            chats={chatsData}
            activeChatId={effectiveChatId}
            onChatSelect={handleChatSelect}
            onDeleteChat={handleDeleteChat}
            isLoading={isChatsLoading}
          />
        </div>
      )}
  {/* Fullscreen viewer */}
  <FullscreenImageSpotlightViewer
    isOpen={viewerOpen}
    imageUrl={viewerImageUrl}
    spotlights={viewerSpotlights}
    onClose={closeViewer}
    highlightSpotlightId={highlightedSpotlightId}
  />

      {/* Objective Fulfillment Notifications */}
      {!!effectiveProjectId && (
        <ParticipantNotificationManager 
          notifications={objectiveNotifications}
          onConsumed={(id) => {
            setChatState(prev => ({
              ...prev,
              objectiveNotifications: prev.objectiveNotifications.filter(n => n.id !== id)
            }));
          }}
        />
      )}
    </div>
  );
};

export default ChatSection;